/*
 * Copyright 2023 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  TimeTicket,
  TimeTicketSize,
  TimeTicketStruct,
} from '@yorkie-js/sdk/src/document/time/ticket';
import {
  VersionVector,
  ticketKnown,
} from '@yorkie-js/sdk/src/document/time/version_vector';
import { CRDTElement } from '@yorkie-js/sdk/src/document/crdt/element';

import type {
  DefaultTextType,
  TreeNodeType,
  TreeToken,
} from '@yorkie-js/sdk/src/util/index_tree';
import {
  IndexTree,
  IndexTreeNode,
  TokenType,
  traverseAll,
  TreePos,
} from '@yorkie-js/sdk/src/util/index_tree';
import { RHT, RHTNode, RHTWrite } from './rht';
import { ActorID } from './../time/actor_id';
import { LLRBTree } from '@yorkie-js/sdk/src/util/llrb_tree';
import { Comparator } from '@yorkie-js/sdk/src/util/comparator';
import {
  parseAttrValue,
  parseObjectValues,
} from '@yorkie-js/sdk/src/util/object';
import { Indexable } from '@yorkie-js/sdk/src/document/document';
import type * as Devtools from '@yorkie-js/sdk/src/devtools/types';
import { ensureUTF16Boundary } from '@yorkie-js/sdk/src/document/json/strings';
import { GCChild, GCPair, GCParent } from '@yorkie-js/sdk/src/document/crdt/gc';
import { Code, YorkieError } from '@yorkie-js/sdk/src/util/error';
import { logger } from '@yorkie-js/sdk/src/util/logger';
import {
  DataSize,
  DocSize,
  addDataSizes,
  subDataSize,
} from '../../util/resource';

/**
 * `TreeNode` represents a node in the tree.
 */
export type TreeNode = TextNode | ElementNode;

/**
 * `ElementNode` represents an element node. It has an attributes and children.
 */
export type ElementNode<A extends Indexable = Indexable> = {
  type: TreeNodeType;
  attributes?: A;
  children: Array<TreeNode>;
};

/**
 * `TextNode` represents a text node. It has a string value.
 */
export type TextNode = {
  type: typeof DefaultTextType;
  value: string;
};

/**
 * `TreeNodeForTest` represents the JSON representation of a node in the tree.
 * It is used for testing.
 */
export type TreeNodeForTest = TreeNode & {
  children?: Array<TreeNodeForTest>;
  visibleSize: number;
  isRemoved: boolean;
};

/**
 * `TreeChangeType` represents the type of change in the tree.
 */
export enum TreeChangeType {
  Content = 'content',
  Style = 'style',
  RemoveStyle = 'removeStyle',
}

/**
 * `TreeChange` represents the change in the tree.
 */
export type TreeChange =
  | {
      actor: ActorID;
      type: TreeChangeType.Content;
      from: number;
      to: number;
      fromPath: Array<number>;
      toPath: Array<number>;
      value?: Array<TreeNode>;
      splitLevel?: number;
    }
  | {
      actor: ActorID;
      type: TreeChangeType.Style;
      from: number;
      to: number;
      fromPath: Array<number>;
      toPath: Array<number>;
      value: { [key: string]: string };
      splitLevel?: number;
    }
  | {
      actor: ActorID;
      type: TreeChangeType.RemoveStyle;
      from: number;
      to: number;
      fromPath: Array<number>;
      toPath: Array<number>;
      value?: Array<string>;
      splitLevel?: number;
    };

/**
 * `TreeVisibleEdit` is a content change an identity-preserving restore or
 * retombstone made, paired with the visible size it inserted (zero for a
 * deletion). The change alone does not carry that size — an insertion reports
 * a collapsed range — and undo-stack reconciliation cannot shift the pending
 * indices without it.
 */
export type TreeVisibleEdit = {
  change: TreeChange;
  insertedSize: number;
};

/**
 * `CRDTTreePos` represent a position in the tree. It is used to identify a
 * position in the tree. It is composed of the parent ID and the left sibling
 * ID. If there's no left sibling in parent's children, then left sibling is
 * parent.
 */
export class CRDTTreePos {
  private parentID: CRDTTreeNodeID;
  private leftSiblingID: CRDTTreeNodeID;

  constructor(parentID: CRDTTreeNodeID, leftSiblingID: CRDTTreeNodeID) {
    this.parentID = parentID;
    this.leftSiblingID = leftSiblingID;
  }

  /**
   * `of` creates a new instance of CRDTTreePos.
   */
  public static of(parentID: CRDTTreeNodeID, leftSiblingID: CRDTTreeNodeID) {
    return new CRDTTreePos(parentID, leftSiblingID);
  }

  /**
   * `fromTreePos` creates a new instance of CRDTTreePos from the given TreePos.
   */
  public static fromTreePos(pos: TreePos<CRDTTreeNode>): CRDTTreePos {
    const { offset } = pos;
    let { node } = pos;
    let leftNode;

    if (node.isText) {
      if (node.parent!.children[0] === node && offset === 0) {
        leftNode = node.parent!;
      } else {
        leftNode = node;
      }

      node = node.parent!;
    } else {
      if (offset === 0) {
        leftNode = node;
      } else {
        leftNode = node.children[offset - 1];
      }
    }

    return CRDTTreePos.of(
      node.id,
      CRDTTreeNodeID.of(leftNode.getCreatedAt(), leftNode.getOffset() + offset),
    );
  }

  /**
   * `getParentID` returns the parent ID.
   */
  public getParentID() {
    return this.parentID;
  }

  /**
   * `fromStruct` creates a new instance of CRDTTreeNodeID from the given struct.
   */
  public static fromStruct(struct: CRDTTreePosStruct): CRDTTreePos {
    return CRDTTreePos.of(
      CRDTTreeNodeID.of(
        TimeTicket.fromStruct(struct.parentID.createdAt),
        struct.parentID.offset,
      ),
      CRDTTreeNodeID.of(
        TimeTicket.fromStruct(struct.leftSiblingID.createdAt),
        struct.leftSiblingID.offset,
      ),
    );
  }

  /**
   * `toStruct` returns the structure of this position.
   */
  public toStruct(): CRDTTreePosStruct {
    return {
      parentID: {
        createdAt: this.getParentID().getCreatedAt().toStruct(),
        offset: this.getParentID().getOffset(),
      },
      leftSiblingID: {
        createdAt: this.getLeftSiblingID().getCreatedAt().toStruct(),
        offset: this.getLeftSiblingID().getOffset(),
      },
    };
  }

  /**
   * `toTreeNodePair` converts the pos to parent and left sibling nodes.
   * If the position points to the middle of a node, then the left sibling node
   * is the node that contains the position. Otherwise, the left sibling node is
   * the node that is located at the left of the position.
   */
  public toTreeNodePair(tree: CRDTTree): TreeNodePair {
    const parentID = this.getParentID();
    const leftSiblingID = this.getLeftSiblingID();
    const parentNode = tree.findFloorNode(parentID);
    let leftNode = tree.findFloorNode(leftSiblingID);
    if (!parentNode || !leftNode) {
      throw new YorkieError(
        Code.ErrRefused,
        `cannot find node of CRDTTreePos(${parentID.toTestString()}, ${leftSiblingID.toTestString()})`,
      );
    }

    /**
     * NOTE(hackerwins): If the left node and the parent node are the same,
     * it means that the position is the left-most of the parent node.
     * We need to skip finding the left of the position.
     */
    if (
      !leftSiblingID.equals(parentID) &&
      leftSiblingID.getOffset() > 0 &&
      leftSiblingID.getOffset() === leftNode.id.getOffset() &&
      leftNode.insPrevID
    ) {
      leftNode = tree.findFloorNode(leftNode.insPrevID)!;
    }

    return [parentNode, leftNode];
  }

  /**
   * `getLeftSiblingID` returns the left sibling ID.
   */
  public getLeftSiblingID() {
    return this.leftSiblingID;
  }

  /**
   * `equals` returns whether the given pos equals to this or not.
   */
  public equals(other: CRDTTreePos): boolean {
    return (
      this.getParentID()
        .getCreatedAt()
        .equals(other.getParentID().getCreatedAt()) &&
      this.getParentID().getOffset() === other.getParentID().getOffset() &&
      this.getLeftSiblingID()
        .getCreatedAt()
        .equals(other.getLeftSiblingID().getCreatedAt()) &&
      this.getLeftSiblingID().getOffset() ===
        other.getLeftSiblingID().getOffset()
    );
  }

  /**
   * `replaceNodeID` returns this position with `prev` replaced by `curr`
   * wherever it names the parent or the left sibling, or this position
   * itself when it names neither.
   */
  public replaceNodeID(
    prev: CRDTTreeNodeID,
    curr: CRDTTreeNodeID,
  ): CRDTTreePos {
    const parentID = replaceTreeNodeID(this.parentID, prev, curr);
    const leftSiblingID = replaceTreeNodeID(this.leftSiblingID, prev, curr);

    return parentID === this.parentID && leftSiblingID === this.leftSiblingID
      ? this
      : CRDTTreePos.of(parentID, leftSiblingID);
  }
}

/**
 * `replaceTreeNodeID` returns `id` re-pointed from the node `prev` names to
 * the node `curr` names, or `id` itself when it names a different node.
 *
 * The match is on the creation ticket alone, and the offset is carried over
 * rather than taken from `curr`: a node id names a node by its creation
 * ticket, while the offset says WHERE in that node the reference lands --
 * the child index when a position names an element as its left sibling
 * (`CRDTTreePos.fromTreePos`), the character offset inside a text node.
 * Comparing the offset too, as `CRDTTreeNodeID.equals` does, would miss every
 * reference that is not to the node's own head.
 */
export function replaceTreeNodeID(
  id: CRDTTreeNodeID,
  prev: CRDTTreeNodeID,
  curr: CRDTTreeNodeID,
): CRDTTreeNodeID {
  if (id.getCreatedAt().compare(prev.getCreatedAt()) !== 0) {
    return id;
  }

  return CRDTTreeNodeID.of(curr.getCreatedAt(), id.getOffset());
}

/**
 * `CRDTTreeNodeID` represent an ID of a node in the tree. It is used to
 * identify a node in the tree. It is composed of the creation time of the node
 * and the offset from the beginning of the node if the node is split.
 *
 * Some of replicas may have nodes that are not split yet. In this case, we can
 * use `map.floorEntry()` to find the adjacent node.
 */
export class CRDTTreeNodeID {
  /**
   * `createdAt` is the creation time of the node.
   */
  private createdAt: TimeTicket;

  /**
   * `offset` is the distance from the beginning of the node if the node is
   * split.
   */
  private offset: number;

  constructor(createdAt: TimeTicket, offset: number) {
    this.createdAt = createdAt;
    this.offset = offset;
  }

  /**
   * `of` creates a new instance of CRDTTreeNodeID.
   */
  public static of(createdAt: TimeTicket, offset: number): CRDTTreeNodeID {
    return new CRDTTreeNodeID(createdAt, offset);
  }

  /**
   * `fromStruct` creates a new instance of CRDTTreeNodeID from the given struct.
   */
  public static fromStruct(struct: CRDTTreeNodeIDStruct): CRDTTreeNodeID {
    return CRDTTreeNodeID.of(
      TimeTicket.fromStruct(struct.createdAt),
      struct.offset,
    );
  }

  /**
   * `createComparator` creates a comparator for CRDTTreeNodeID.
   */
  public static createComparator(): Comparator<CRDTTreeNodeID> {
    return (idA: CRDTTreeNodeID, idB: CRDTTreeNodeID) => {
      const compare = idA.getCreatedAt().compare(idB.getCreatedAt());
      if (compare !== 0) {
        return compare;
      }
      if (idA.getOffset() > idB.getOffset()) {
        return 1;
      } else if (idA.getOffset() < idB.getOffset()) {
        return -1;
      }
      return 0;
    };
  }

  /**
   * `getCreatedAt` returns the creation time of the node.
   */
  public getCreatedAt(): TimeTicket {
    return this.createdAt;
  }

  /**
   * `equals` returns whether given ID equals to this ID or not.
   */
  public equals(other: CRDTTreeNodeID): boolean {
    return (
      this.createdAt.compare(other.createdAt) === 0 &&
      this.offset === other.offset
    );
  }

  /**
   * `getOffset` returns returns the offset of the node.
   */
  public getOffset(): number {
    return this.offset;
  }

  /**
   * `setOffset` sets the offset of the node.
   */
  public setOffset(offset: number): void {
    this.offset = offset;
  }

  /**
   * `toStruct` returns the structure of this position.
   */
  public toStruct(): CRDTTreeNodeIDStruct {
    return {
      createdAt: this.createdAt.toStruct(),
      offset: this.offset,
    };
  }

  /**
   * `toIDString` returns a string that can be used as an ID for this position.
   */
  public toIDString(): string {
    return `${this.createdAt.toIDString()}:${this.offset}`;
  }

  /**
   * `toTestString` returns a string containing the meta data of the ticket
   * for debugging purpose.
   */
  public toTestString(): string {
    return `${this.createdAt.toTestString()}/${this.offset}`;
  }
}

/**
 * `CRDTTreePosStruct` represents the structure of CRDTTreePos.
 */
export type CRDTTreePosStruct = {
  parentID: CRDTTreeNodeIDStruct;
  leftSiblingID: CRDTTreeNodeIDStruct;
};

/**
 * `CRDTTreeNodeIDStruct` represents the structure of CRDTTreeNodeID.
 * It is used to serialize and deserialize the CRDTTreeNodeID.
 */
export type CRDTTreeNodeIDStruct = {
  createdAt: TimeTicketStruct;
  offset: number;
};

/**
 * `TreePosRange` represents a pair of CRDTTreePos.
 */
export type TreePosRange = [CRDTTreePos, CRDTTreePos];

/**
 * `TreeNodePair` represents a pair of CRDTTreeNode. It represents the position
 * of the node in the tree with the left and parent nodes.
 */
type TreeNodePair = [CRDTTreeNode, CRDTTreeNode];

/**
 * `TreePosStructRange` represents the structure of TreeRange.
 * It is used to serialize and deserialize the TreeRange.
 */
export type TreePosStructRange = [CRDTTreePosStruct, CRDTTreePosStruct];

/**
 * `TreeRestoreSpan` identifies a node this edit transitioned
 * visible → tombstoned, for identity-preserving Tree undo/redo. For text
 * nodes the span is the absolute-offset interval [id.offset, id.offset +
 * length) of the original insertion (split-invariant); for element nodes it
 * is the whole node. `value`/`attrs` are deep-copied so a GC-purged node can
 * be recreated. `leftSiblingID`/`rightSiblingID` are the deleted run's
 * external boundary anchors captured at tombstone time — redundant on
 * purpose: since a run's spans are carried together, restore can rebuild the
 * run's internal order from the op itself and needs only ONE surviving
 * boundary to place it (id-order is not sibling-order in a tree).
 */
export type TreeRestoreSpan = {
  id: CRDTTreeNodeID;
  nodeType: string;
  isText: boolean;
  length: number; // text length; 0 for elements
  value?: string; // text content copy
  attrs?: RHT; // element attribute snapshot
  parentID?: CRDTTreeNodeID;
  leftSiblingID?: CRDTTreeNodeID; // undefined → was first child
  rightSiblingID?: CRDTTreeNodeID; // undefined → was last child
};

/**
 * `CRDTTreeNode` is a node of CRDTTree. It includes the logical clock and
 * links to other nodes to resolve conflicts.
 */
export class CRDTTreeNode
  extends IndexTreeNode<CRDTTreeNode>
  implements GCParent, GCChild
{
  id: CRDTTreeNodeID;
  removedAt?: TimeTicket;
  attrs?: RHT;

  /**
   * `insPrevID` is the previous node id of this node after the node is split.
   */
  insPrevID?: CRDTTreeNodeID;

  /**
   * `insNextID` is the previous node id of this node after the node is split.
   */
  insNextID?: CRDTTreeNodeID;

  /**
   * `mergedFrom` records the parent this node logically belongs to when
   * a merge relocated it into the merge target: the source parent it was
   * moved out of, or the declared parent of an insert redirected by the
   * §9.4 intended-parent stamp. Persisted in the snapshot encoding as
   * the witness of the merge relationship.
   */
  mergedFrom?: CRDTTreeNodeID;

  /**
   * `mergedAt` records the immutable ticket of the merge operation.
   * Persisted alongside `mergedFrom` because the source parent's
   * `removedAt` may be overwritten by later LWW tombstones and thus
   * cannot serve as the merge-time causal boundary for splitElement's
   * Fix 8 version-vector check.
   */
  mergedAt?: TimeTicket;

  /**
   * `mergedInto` is a runtime cache set on the source parent pointing
   * at the merge target. Set locally during merge execution and rebuilt
   * from `mergedFrom` on snapshot load — on both paths only while the
   * source is a tombstone, so `unremove` drops it again. Used for the fast
   * "is this tombstoned parent a merge source?" check in
   * `FindTreeNodesWithSplitText`; the alternative (scanning
   * `nodeMapByID` on every position resolution) would be too expensive.
   */
  mergedInto?: CRDTTreeNodeID;

  _value = '';

  constructor(
    id: CRDTTreeNodeID,
    type: string,
    opts?: string | Array<CRDTTreeNode>,
    attributes?: RHT,
    removedAt?: TimeTicket,
  ) {
    super(type);
    this.id = id;
    this.removedAt = removedAt;
    if (attributes) {
      this.attrs = attributes;
    }

    if (typeof opts === 'string') {
      this.value = opts;
    } else if (Array.isArray(opts)) {
      this._children = opts;
    }
  }

  /**
   * `toIDString` returns the IDString of this node.
   */
  toIDString(): string {
    return this.id.toIDString();
  }

  /**
   * `getRemovedAt` returns the time when this node was removed.
   */
  getRemovedAt(): TimeTicket | undefined {
    return this.removedAt;
  }

  /**
   * `create` creates a new instance of CRDTTreeNode.
   */
  static create(
    id: CRDTTreeNodeID,
    type: string,
    opts?: string | Array<CRDTTreeNode>,
    attributes?: RHT,
  ) {
    return new CRDTTreeNode(id, type, opts, attributes);
  }

  /**
   * `deepcopy` copies itself deeply.
   */
  deepcopy(): CRDTTreeNode {
    const clone = new CRDTTreeNode(this.id, this.type);
    clone.removedAt = this.removedAt;
    clone._value = this._value;
    clone.visibleSize = this.visibleSize;
    clone.totalSize = this.totalSize;
    clone.attrs = this.attrs?.deepcopy();
    clone._children = this._children.map((child) => {
      const childClone = child.deepcopy();
      childClone.parent = clone;
      return childClone;
    });
    clone.insPrevID = this.insPrevID;
    clone.insNextID = this.insNextID;
    clone.mergedFrom = this.mergedFrom;
    clone.mergedAt = this.mergedAt;
    clone.mergedInto = this.mergedInto;
    return clone;
  }

  /**
   * `value` returns the value of the node.
   */
  get value() {
    if (!this.isText) {
      throw new YorkieError(
        Code.ErrInvalidType,
        `cannot get value of element node: ${this.type}`,
      );
    }

    return this._value;
  }

  /**
   * `value` sets the value of the node.
   */
  set value(v: string) {
    if (!this.isText) {
      throw new YorkieError(
        Code.ErrInvalidType,
        `cannot set value of element node: ${this.type}`,
      );
    }

    this._value = v;
    this.visibleSize = v.length;
    this.totalSize = v.length;
  }

  /**
   * `dropSplitLinks` clears the split-sibling links on this node and every one
   * of its descendants.
   *
   * insPrevID/insNextID name positions in a split chain and only
   * `splitElement` may create them. A node arriving as operation content is
   * freshly created by the editing client, so it can never legitimately be a
   * split product — but the wire format carries the links regardless, and the
   * walks that read them treat them as trusted structural pointers.
   *
   * The merge lineage is deliberately left alone here: unlike operation
   * content, an element payload can be a deep copy of real document state
   * (the reverse of a Remove restores the tree as it stood, merges and all),
   * and erasing mergedFrom/mergedAt would switch off the §1.1 insert redirect
   * and the §6.2 propagation skip for a tree that legitimately earned them.
   * Only `dropEngineOnlyLinks`, which runs on content no merge can have
   * touched, clears those.
   */
  public dropSplitLinks(): void {
    traverseAll(this as CRDTTreeNode, (node: CRDTTreeNode) => {
      node.insPrevID = undefined;
      node.insNextID = undefined;
    });
  }

  /**
   * `dropEngineOnlyLinks` clears the engine-only links on this node and every
   * one of its descendants: the split-sibling chain and the merge lineage.
   *
   * It is the operation-content counterpart of `dropSplitLinks`.
   * `mergedFrom`/`mergedAt` name the parent a node was moved out of and only
   * a merge may stamp them (`edit` restamps them on the content it inserts,
   * from the merge parent it resolves locally), so content arriving on a
   * TreeEdit -- always freshly created by the editing client, never a copy of
   * live state -- can never legitimately carry them, while the §1.1 redirect
   * and §6.2 propagation read them as trusted structural pointers.
   * `mergedInto` goes with them: the decoder derives it from `mergedFrom`, so
   * leaving it would keep a source pointing at a destination no field records
   * any more.
   */
  public dropEngineOnlyLinks(): void {
    traverseAll(this as CRDTTreeNode, (node: CRDTTreeNode) => {
      node.insPrevID = undefined;
      node.insNextID = undefined;
      node.mergedFrom = undefined;
      node.mergedAt = undefined;
      node.mergedInto = undefined;
    });
  }

  /**
   * `clearTombstones` clears the tombstone on this node and every one of its
   * descendants, restoring the sizes a decoded tombstone suppressed.
   *
   * Like the engine-only links, `removedAt` is carried by the wire format on
   * every tree node yet operation content can never legitimately hold it: a
   * TreeEdit's content is freshly created by the editing client, so a node
   * arriving tombstoned is a crafted one. Left in place, `edit` counts it
   * into the live data size and registers no GC pair for it under a live
   * parent, while `isRemoved` hides it from every later edit.
   *
   * Cleared via `unremove` so each node's padded size is given back to its
   * ancestors. `traverseAll` is post-order, so a child is revived before its
   * parent, and the parent's own size already includes the child when the
   * parent hands it further up.
   *
   * A node's attribute table carries tombstones of its own, and those are
   * deliberately NOT cleared here. Unlike `removedAt` on the node, a removed
   * RHT entry on content can be genuine: the undo copy-reinsert path re-sends
   * a `deepcopy` of nodes a real `removeStyle` tombstoned, and it has to, or
   * the reinserted node would stop rejecting the stale styles the original
   * rejects. Stripping them on decode would also make this replica disagree
   * with every other producer of the same bytes -- an older yorkie-js-sdk, the
   * Go SDK, and the snapshot the server rebuilds from this very operation --
   * which is divergence, not hardening. A crafted one is instead made
   * harmless the way every other decode path already makes it harmless:
   * `edit` books it into gc through `CRDTTreeNode.getGCPairs`, the same
   * routing the snapshot and the Set/Add element payload take through
   * `CRDTTree.getGCPairs`.
   */
  public clearTombstones(): void {
    traverseAll(this as CRDTTreeNode, (node: CRDTTreeNode) => {
      node.unremove();
    });
  }

  /**
   * `isRemoved` returns whether the node is removed or not.
   */
  get isRemoved(): boolean {
    return !!this.removedAt;
  }

  /**
   * `remove` marks the node as removed.
   */
  remove(removedAt: TimeTicket): boolean {
    if (!this.removedAt) {
      this.removedAt = removedAt;
      // NOTE(hackerwins): Decrease only visibleSize because
      // this node marked as tombstone, not purged.
      this.updateAncestorsSize(-this.paddedSize());
      return true;
    }

    // NOTE(sigmaith): Overwrite if newer tombstone.
    // This enables LWW for concurrent deletions.
    if (removedAt.after(this.removedAt)) {
      this.removedAt = removedAt;
    }
    return false;
  }

  /**
   * `unremove` clears the tombstone of this node (identity-preserving
   * restore). Mirrors `remove()`'s ancestor-size bookkeeping so the node
   * becomes visible again in place.
   *
   * The forwarding pointer goes with the tombstone: both writers of
   * `mergedInto` (the merge in `edit` and `rebuildMergeState`) only ever set
   * it on a source that is already removed, so a live node holding one is a
   * pointer a snapshot-loading replica would not rebuild. `CRDTTree.restore`
   * drops the matching `mergedFrom` stamps on the children the merge moved,
   * so nothing is left for a later re-remove to derive it from either.
   */
  unremove(): void {
    if (!this.removedAt) {
      return;
    }
    this.removedAt = undefined;
    this.mergedInto = undefined;
    this.updateAncestorsSize(this.paddedSize());
  }

  /**
   * `cloneText` clones this text node with the given offset.
   */
  cloneText(offset: number): CRDTTreeNode {
    const clone = new CRDTTreeNode(
      CRDTTreeNodeID.of(this.id.getCreatedAt(), offset),
      this.type,
      undefined,
      undefined,
      this.removedAt,
    );
    clone.mergedFrom = this.mergedFrom;
    clone.mergedAt = this.mergedAt;
    return clone;
  }

  /**
   * `cloneElement` clones this element node with the given issueTimeTicket function.
   */
  cloneElement(issueTimeTicket: () => TimeTicket): CRDTTreeNode {
    const clone = new CRDTTreeNode(
      CRDTTreeNodeID.of(issueTimeTicket(), 0),
      this.type,
      undefined,
      this.attrs?.deepcopy(),
      this.removedAt,
    );
    // The split product holds the other half of the same moved node, so
    // it carries the same merge stamp (as cloneText does).
    clone.mergedFrom = this.mergedFrom;
    clone.mergedAt = this.mergedAt;
    return clone;
  }

  /**
   * `split` splits the given offset of this node.
   */
  public split(
    tree: CRDTTree,
    offset: number,
    issueTimeTicket?: () => TimeTicket,
    versionVector?: VersionVector,
  ): [CRDTTreeNode | undefined, DataSize] {
    const [split, diff] = this.isText
      ? this.splitText(offset, this.id.getOffset())
      : this.splitElement(offset, issueTimeTicket!, versionVector);

    if (split) {
      split.insPrevID = this.id;
      if (this.insNextID) {
        const insNext = tree.findFloorNode(this.insNextID);
        split.insNextID = this.insNextID;

        if (insNext) {
          insNext.insPrevID = split.id;

          // §7.4 Empty Sibling Re-Parenting: When the existing
          // InsNext sibling is in a different parent (due to a prior
          // parent-level split), move the new empty split sibling to
          // that parent. VV-independent for clone/root consistency.
          // Skip when insNext has been tombstoned (e.g. by an undo
          // boundary deletion): re-parenting into a removed element
          // would make the new split sibling invisible.
          if (
            !this.isText &&
            insNext.parent &&
            !insNext.isRemoved &&
            insNext.parent !== split.parent &&
            split.allChildren.length === 0
          ) {
            // Moved rather than detached and re-inserted: a split born
            // tombstoned contributes no visibleSize to either parent, so
            // detaching would take its tokens off the source that it never
            // held and re-inserting would give the destination tokens it must
            // not have. `moveChildBefore` carries the same tombstone-aware
            // semantics `moveChild` documents.
            insNext.parent.moveChildBefore(split, insNext);
          }
        }
      }
      this.insNextID = split.id;
      tree.registerNode(split);

      // NOTE: `splitElement` deep-copies the node's attributes, tombstones
      // included -- it has to, or the two halves of what was one node would
      // resolve a concurrent style differently and never reconverge. Each
      // copied tombstone is a distinct piece of garbage that no removal path
      // produced, so buffer a registration for it; otherwise it sits in the
      // split's RHT forever, uncounted and unpurgeable.
      //
      // `getDataSize` skips removed attributes, so the split's diff never
      // charged these to docSize.live -- `gcOnlySize` sends each straight to
      // docSize.gc, and purge subtracts the same amount back. This is the
      // same routing `getGCPairs` already uses for the tombstones a snapshot
      // rebuild finds.
      for (const pair of split.getGCPairs()) {
        tree.pushPendingGCPair(pair);
      }

      // NOTE: A piece split off an already-tombstoned node inherits
      // `removedAt` without going through `remove()`, so no GC pair is
      // created for it in the normal deletion path. Register it here so
      // it can be purged; otherwise it stays in the tree forever.
      // The piece was never live, so its size goes straight to docSize.gc
      // when the pair is registered; report a zero diff to the caller
      // (which accounts diffs to docSize.live).
      if (split.removedAt) {
        tree.registerPendingGCPair(split, diff);
        return [split, { data: 0, meta: 0 }];
      }
    }
    return [split, diff];
  }

  /**
   * `getCreatedAt` returns the creation time of this element.
   */
  public getCreatedAt(): TimeTicket {
    return this.id.getCreatedAt();
  }

  /**
   * `getOffset` returns the offset of a pos.
   */
  public getOffset(): number {
    return this.id.getOffset();
  }

  /**
   * `canDelete` checks if node is able to delete.
   */
  public canDelete(
    editedAt: TimeTicket,
    creationKnown: boolean,
    tombstoneKnown: boolean,
  ): boolean {
    // NOTE(sigmaith): Skip if the node's creation was not visible to this operation.
    if (!creationKnown) {
      return false;
    }

    if (!this.removedAt) {
      return true;
    }

    // NOTE(sigmaith): Overwrite only if prior tombstone was not known
    // (concurrent or unseen) and newer. This enables LWW for concurrent deletions.
    if (!tombstoneKnown && editedAt?.after(this.removedAt)) {
      return true;
    }
    return false;
  }

  /**
   * `canStyle` checks if node is able to style. It answers the same question
   * as `RGATreeSplitNode.canStyle`, the same way — see the contract there.
   */
  public canStyle(versionVector?: VersionVector): boolean {
    return !this.isText && ticketKnown(versionVector, this.getCreatedAt());
  }

  /**
   * `setAttrs` sets the attributes of the node.
   */
  public setAttrs(
    attrs: { [key: string]: string },
    editedAt: TimeTicket,
  ): Array<RHTWrite> {
    if (!this.attrs) {
      this.attrs = new RHT();
    }

    const writes: Array<RHTWrite> = [];
    for (const [key, value] of Object.entries(attrs)) {
      writes.push(this.attrs.set(key, value, editedAt));
    }

    return writes;
  }

  /**
   * `purge` purges the given child node.
   */
  public purge(node: RHTNode): void {
    if (this.attrs) {
      this.attrs.purge(node);
    }
  }

  /**
   * `getDataSize` returns the data size of the node.
   */
  public getDataSize(): DataSize {
    const dataSize = { data: 0, meta: 0 };

    if (this.isText) {
      dataSize.data += this.visibleSize * 2;
    }

    if (this.id) {
      dataSize.meta += TimeTicketSize;
    }

    if (this.removedAt) {
      dataSize.meta += TimeTicketSize;
    }

    if (this.attrs) {
      for (const node of this.attrs) {
        if (node.getRemovedAt()) {
          continue;
        }

        const size = node.getDataSize();
        dataSize.meta += size.meta;
        dataSize.data += size.data;
      }
    }

    return dataSize;
  }

  /**
   * `getGCPairs` returns the pairs of GC.
   */
  public getGCPairs(): Array<GCPair> {
    const pairs: Array<GCPair> = [];
    if (!this.attrs) {
      return pairs;
    }

    // NOTE: Removed attribute nodes are skipped by `getDataSize`, so they
    // were never counted into docSize.live — hence `gcOnlySize`.
    for (const node of this.attrs) {
      if (node.getRemovedAt()) {
        pairs.push({
          parent: this,
          child: node,
          gcOnlySize: node.getDataSize(),
        });
      }
    }

    return pairs;
  }
}

/**
 * `attrGCPair` builds the GC pair for an RHT node a style edit turned into
 * garbage, deciding which half of the ledger it moves through. Three cases,
 * because the NODE holding the attribute may itself be a tombstone —
 * `canStyle` admits one removed concurrently with the change:
 *
 * - live attr on a live node — in live, so move live → gc the usual way.
 * - live attr on a REMOVED node — the container skips a removed node
 *   (`CRDTText.getDataSize`, `CRDTTree.getDataSize`; the per-NODE functions
 *   count a live attribute either way), so it is not in live; its bytes are
 *   already inside the gc charge taken when the node was removed, and
 *   removing the attribute shrinks that charge by exactly them. Charging them
 *   again doubles them, so the pair carries exactly zero.
 * - attr that was already a tombstone — never in live, and not inside the
 *   node's charge either, so it carries its own size.
 *
 * Charging case 2 or 3 to live walked the live size down by the attribute's
 * size on every such edit, without bound — a rich-text editor toggling one key
 * is exactly that loop — and drove it negative, at which point the document
 * size limit stops applying.
 *
 * Shared by the tree and the text halves: both hold attributes in an RHT and
 * must answer this the same way, which they historically did not (#1341).
 */
export function attrGCPair(
  parent: GCParent,
  child: RHTNode,
  attrWasLive: boolean,
  nodeIsLive: boolean,
): GCPair {
  if (attrWasLive && nodeIsLive) {
    return { parent, child };
  }

  // A live attribute on a removed node is already counted inside that node's
  // gc charge.
  const gcOnlySize = attrWasLive ? { data: 0, meta: 0 } : child.getDataSize();
  return { parent, child, gcOnlySize };
}

/**
 * `accAttrWrite` books one attribute write into the ledger. It reads only what
 * the write reported, never the map: a write that lost LWW installed nothing,
 * so it must charge nothing, and a node visited twice in one traversal (once
 * as Start and once as End) loses LWW on the second visit and is naturally
 * deduped. Deciding from the map instead made live depend on delivery order
 * and, where a token guard suppressed only one half, drove it negative.
 */
export function accAttrWrite(
  write: RHTWrite,
  parent: GCParent,
  nodeIsLive: boolean,
  pairs: Array<GCPair>,
  size: DocSize,
): void {
  if (write.revived !== undefined) {
    pairs.push(attrGCPair(parent, write.revived, false, nodeIsLive));
  }

  // `nodeIsLive` is false when the container does not count this node's
  // attributes in live at all -- a tombstoned node, which `CRDTText
  // .getDataSize` and `CRDTTree.getDataSize` both skip. (The per-NODE
  // functions do NOT skip; the exclusion lives in the container.) Booking
  // either half to live there drifts it by the SIGNED difference between the
  // two values' sizes, and a shrinking overwrite takes it negative.
  //
  // The bytes are not nowhere, though: they are inside the gc charge the
  // node's removal took, and collection subtracts the node's size as it
  // stands when it is purged. So the same delta goes to gc, or registration
  // and purge stop agreeing about that one node -- which a style concurrent
  // with a removal reaches on the replica that receives it, see `canStyle`.
  const target = nodeIsLive ? size.live : size.gc;

  if (write.superseded !== undefined) {
    subDataSize(target, write.superseded.getDataSize());
  }
  if (write.installed !== undefined) {
    addDataSizes(target, write.installed.getDataSize());
  }
}

/**
 * `toTreeNode` converts the given CRDTTreeNode to TreeNode.
 */
function toTreeNode(node: CRDTTreeNode): TreeNode {
  if (node.isText) {
    const currentNode = node;
    return {
      type: currentNode.type,
      value: currentNode.value,
    } as TextNode;
  }

  const treeNode: TreeNode = {
    type: node.type,
    children: node.children.map(toTreeNode),
  };

  if (node.attrs) {
    treeNode.attributes = parseObjectValues(node.attrs?.toObject());
  }

  return treeNode;
}

const xmlEscapes: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&apos;',
};

/**
 * `escapeXMLAttr` escapes a name or a value for interpolation into a
 * double-quoted XML attribute.
 *
 * Both halves are peer-chosen: the key is whatever `Style` was called with,
 * and the value is what `parseAttrValue` read back -- including, for a peer
 * that stores values raw, the raw string itself. Interpolated unescaped, a
 * `"` closes the attribute and a `<` opens an element, so an attribute could
 * forge structure in the markup `toXML` builds. This is the same forging
 * `CRDTTextValue.toJSON` closes for the JSON encoding of a Text attribute.
 */
function escapeXMLAttr(value: string): string {
  return value.replace(/[&<>"']/g, (character) => xmlEscapes[character]);
}

/**
 * `toXML` converts the given CRDTNode to XML string.
 */
export function toXML(node: CRDTTreeNode): string {
  if (node.isText) {
    const currentNode = node;
    return currentNode.value;
  }

  let attrs = '';
  if (node.attrs && node.attrs.size()) {
    attrs =
      ' ' +
      Array.from(node.attrs)
        .filter((n) => !n.isRemoved())
        .sort((a, b) => a.getKey().localeCompare(b.getKey()))
        .map((n) => {
          // See `parseAttrValue`: a peer that stores values raw writes ones
          // this cannot parse, and rendering must not throw on them. The raw
          // string it falls back to is rendered as the value; anything else
          // keeps the JSON form it is stored as.
          const obj = parseAttrValue(n.getValue());
          const value = typeof obj === 'string' ? obj : n.getValue();
          return `${escapeXMLAttr(n.getKey())}="${escapeXMLAttr(value)}"`;
        })
        .join(' ');
  }

  return `<${node.type}${attrs}>${node.children
    .map((child) => toXML(child))
    .join('')}</${node.type}>`;
}

/**
 * `toTestTreeNode` converts the given CRDTNode JSON for debugging.
 */
function toTestTreeNode(node: CRDTTreeNode): TreeNodeForTest {
  if (node.isText) {
    const currentNode = node;
    return {
      type: currentNode.type,
      value: currentNode.value,
      visibleSize: currentNode.visibleSize,
      isRemoved: currentNode.isRemoved,
    } as TreeNodeForTest;
  }

  return {
    type: node.type,
    children: node.children.map(toTestTreeNode),
    visibleSize: node.visibleSize,
    isRemoved: node.isRemoved,
  };
}

/**
 * `InsNextWalker` bounds a walk of an insNextID chain.
 *
 * insNextID is a structural pointer that only `splitElement` is supposed to
 * set, but it also arrives verbatim from client-supplied bytes, so a chain
 * that loops back on itself would spin the applying task forever. Every
 * chain walk runs through one of these.
 */
class InsNextWalker {
  private seen: Set<CRDTTreeNode> = new Set();

  /**
   * `visit` records `node` and reports whether this walk had not already
   * passed through it. A false result means the chain is cyclic; stop
   * following it.
   */
  public visit(node: CRDTTreeNode): boolean {
    if (this.seen.has(node)) {
      return false;
    }
    this.seen.add(node);

    return true;
  }
}

/**
 * `DeclaredLineage` answers, for one change, the two questions `styleTargets`
 * asks of every node it visits: is this node the element a position declared
 * as its parent (or an ancestor of it), matched through the split lineage.
 *
 * Both questions used to be answered per node by walking the declared parent's
 * whole current ancestry and, for each ancestor, its `insPrevID` chain --
 * pointers a peer controls, so one style message over a wide range bought
 * O(range x depth x chain) work, with a fresh cycle-guard set per step, on
 * every replica that applied it. Nothing in those walks depends on the node
 * being asked about, so they run once per change here: `covers` is then a set
 * lookup, and `coversEitherWay`'s one remaining direction is memoized per
 * node, so each node costs its own chain at most once.
 */
interface DeclaredLineage {
  /**
   * `covers` reports whether `node` is one of the declared ancestors, or a
   * piece a split cut off one of them.
   */
  covers(node: CRDTTreeNode): boolean;

  /**
   * `coversEitherWay` is `covers` plus the opposite direction: it also matches
   * when a declared ancestor is a piece a split cut off `node`.
   */
  coversEitherWay(node: CRDTTreeNode): boolean;
}

/**
 * `CRDTTree` is a CRDT implementation of a tree.
 */
export class CRDTTree extends CRDTElement implements GCParent {
  private indexTree: IndexTree<CRDTTreeNode>;
  private nodeMapByID: LLRBTree<CRDTTreeNodeID, CRDTTreeNode>;

  /**
   * `pendingGCPairs` buffers GC pairs for nodes that were created
   * already-tombstoned by splitting a removed node. Such pieces inherit
   * `removedAt` without ever passing through `remove()`, so they would
   * otherwise never be registered for GC. `edit` and `style` drain this
   * buffer into their returned GC pairs.
   */
  private pendingGCPairs: Array<GCPair>;

  constructor(root: CRDTTreeNode, createdAt: TimeTicket) {
    super(createdAt);
    this.indexTree = new IndexTree<CRDTTreeNode>(root);
    this.nodeMapByID = new LLRBTree(CRDTTreeNodeID.createComparator());
    this.pendingGCPairs = [];

    // Registering every node is the cost of loading a document, so it runs
    // without the duplicate check: a plain put per node, then one comparison
    // to see whether any ID was claimed twice. Only a tree that carries
    // duplicates pays for resolving them.
    let nodeCount = 0;
    this.indexTree.traverseAll((node) => {
      this.nodeMapByID.put(node.id, node);
      nodeCount++;
    });
    if (this.nodeMapByID.size() !== nodeCount) {
      this.indexTree.traverseAll((node) => {
        this.registerNode(node);
      });
    }

    // Rebuild runtime merge state from the persisted `mergedFrom`
    // field. Only `mergedFrom` and `mergedAt` are written to the
    // snapshot encoding; `mergedInto` is a cache reconstructed here
    // so replicas loaded from a snapshot can still handle concurrent
    // ops that target merged-away parents (Fix 3 redirect, Fix 5
    // propagation, Fix 8 split skip).
    this.rebuildMergeState();
  }

  /**
   * `rebuildMergeState` reconstructs the `mergedInto` cache on source
   * parents from the persisted `mergedFrom` field on moved children.
   * For snapshots written before `mergedAt` was added to the proto,
   * it also falls back to the source's `removedAt` — an approximation
   * that may be wrong if the source was later overwritten by a
   * concurrent delete, but this is the best we can do without the
   * persisted merge ticket.
   */
  private rebuildMergeState(): void {
    this.indexTree.traverseAll((node) => {
      if (!node.mergedFrom || !node.parent) {
        return;
      }
      // `findMergeNode`, not `findFloorNode`: `mergedFrom` is client-supplied
      // on an element payload (Set/Add/ArraySet), so the source it names has
      // to be the node it names exactly, and an element.
      const src = this.findMergeNode(node.mergedFrom);
      if (!src) {
        return;
      }

      // A merge moves children under an element parent, so a child sitting
      // under a text node cannot be one a merge moved. Only a snapshot is
      // guaranteed well-formed here: an element payload passes through this
      // same reader and keeps its `mergedFrom`, because the reverse of a
      // Remove legitimately carries the merges the tree really underwent.
      // Deriving a forwarding pointer at a text node from a crafted one would
      // hand a later insert a parent that can hold no children.
      if (node.parent.isText) {
        return;
      }

      // A merge only ever names a source the merge itself removed: the
      // boundary element is tombstoned in step 02 of `edit` before its
      // children are moved in step 03, so a live element is never a merge
      // source. `mergedFrom` is client-supplied on an element payload, so
      // without this check a crafted one plants a forwarding pointer on a
      // live, unrelated element -- arming the §1.1 position redirect and the
      // §6.2 delete cascade to fire on that element's own live children the
      // moment some later, innocent edit removes it.
      if (!src.removedAt) {
        return;
      }

      // Back-compat: older snapshots lack mergedAt on moved children.
      if (!node.mergedAt) {
        node.mergedAt = src.removedAt;
      }

      if (!src.mergedInto) {
        src.mergedInto = node.parent.id;
      }
    });
  }

  /**
   * `create` creates a new instance of `CRDTTree`.
   */
  public static create(root: CRDTTreeNode, ticket: TimeTicket): CRDTTree {
    return new CRDTTree(root, ticket);
  }

  /**
   * `findFloorNode` finds node of given id.
   */
  public findFloorNode(id: CRDTTreeNodeID): CRDTTreeNode | undefined {
    const entry = this.nodeMapByID.floorEntry(id);
    if (!entry || !entry.key.getCreatedAt().equals(id.getCreatedAt())) {
      return;
    }

    return entry.value;
  }

  /**
   * `findMergeNode` returns the element whose id is exactly `id`, or
   * undefined. Unlike `findFloorNode` it refuses a floor-only match (a split
   * product this replica does not hold) and a text node: a merge pointer
   * names an element, so either would hand merge logic a node the pointer
   * never named.
   *
   * Every merge-lineage read goes through here, not only the decode that
   * first sees a pointer: `mergedFrom` is retained on an element payload
   * (Set/Add/ArraySet), persists on the node inside the live document, and
   * `mergedInto` is derived from it. Otherwise a made-up offset becomes a
   * forwarding pointer planted on an unrelated node, which a later, innocent
   * delete follows to tombstone that node's live children.
   */
  private findMergeNode(id?: CRDTTreeNodeID): CRDTTreeNode | undefined {
    if (!id) {
      return;
    }
    const node = this.findFloorNode(id);
    if (!node || !node.id.equals(id) || node.isText) {
      return;
    }
    return node;
  }

  /**
   * `declaredBoundaries` returns the elements the edit's own positions named
   * as boundaries: the element each position declared as its parent, and
   * every ancestor of that element. A range stops at those rather than
   * covering them -- the edit asks to merge their remaining content away, not
   * to delete it -- so a concurrent merge that already moved their children
   * where this edit would have put them did this edit's work rather than
   * something it now has to undo.
   *
   * The declared parent is resolved with `findMergeNode`, not a floor lookup:
   * a floor lookup matches on createdAt alone, so a parentID naming a split
   * product this replica does not hold would land on the offset-0 element and
   * hand the skip to a node the position never named.
   *
   * The walk upward prefers `mergedFrom` over the physical parent: a prior
   * merge moves a node under the merge target, so the parent no longer names
   * the element that enclosed it when the position was declared. The set
   * doubles as the seen set, guarding against a cycle in a client-supplied
   * `mergedFrom` chain. Mirrors yorkie's `declaredBoundaries` (yorkie#2042).
   */
  private declaredBoundaries(
    ...positions: Array<CRDTTreePos>
  ): Set<CRDTTreeNode> {
    const boundaries = new Set<CRDTTreeNode>();
    for (const pos of positions) {
      let current = this.findMergeNode(pos.getParentID());
      while (current && !boundaries.has(current)) {
        boundaries.add(current);
        current =
          this.findMergeNode(current.mergedFrom) ??
          (current.parent as CRDTTreeNode | undefined);
      }
    }
    return boundaries;
  }

  /**
   * `resolveMergeTarget` follows the `mergedInto` forwarding chain from the
   * given node while the current node is a merge-away tombstone, returning
   * the final live target. When a merge lands on a parent that a prior
   * concurrent merge already merged away (a chained merge P->Q->R, applied
   * Q->R before this P->Q), the children must flow to that parent's final
   * destination so the merge chain stays flat (P->R, not P->Q) and both
   * replicas converge. The seen set guards against cycles from a concurrent
   * mutual merge.
   *
   * Each link is resolved through `findMergeNode`, so a text node or a
   * floor-only match cuts the chain: `mergedInto` is derived from the
   * `mergedFrom` an element payload keeps, so a crafted one can name either,
   * and the merge hands what this returns straight to `moveChild`.
   */
  private resolveMergeTarget(node: CRDTTreeNode): CRDTTreeNode {
    let target = node;
    const seen = new Set<CRDTTreeNode>([target]);
    while (target.isRemoved && target.mergedInto) {
      const next = this.findMergeNode(target.mergedInto);
      if (!next || seen.has(next)) {
        break;
      }
      seen.add(next);
      target = next;
    }
    return target;
  }

  /**
   * `mergeSourceOf` returns the id of the removed element whose children a
   * split has just taken back, or undefined when the split re-creates
   * nothing.
   *
   * A merge moves a boundary element's children into the merge target and
   * tombstones the element, stamping each moved child with `mergedFrom`
   * naming it. Reversing that merge is a split, which mints a BRAND-NEW
   * element -- it cannot revive the tombstone, whose id belongs to a change
   * every replica already applied -- and moves the stamped children into it.
   * So a split product holding children stamped for a removed element is the
   * replacement for that element, and anything recorded against the element
   * has to follow (#1425).
   *
   * Derived from the tree rather than carried on the operation because a peer
   * has to reach the same conclusion from the change alone: the operation
   * encodes only the split's tickets, never what they replace.
   *
   * Narrow on purpose. A split that merely happens to cut through a merge
   * target keeps some of the source's children on the left, and its product
   * is a new sibling rather than the source's stand-in -- re-pointing at it
   * would move references onto a node holding only part of what they named.
   * Only a split that takes the source's children back WHOLE reverses the
   * merge, so `splitTarget` is checked for leftovers.
   */
  private mergeSourceOf(
    splitNode: CRDTTreeNode,
    splitTarget: CRDTTreeNode,
  ): CRDTTreeNodeID | undefined {
    const stampedFor = (node: CRDTTreeNode, id: CRDTTreeNodeID) =>
      node.allChildren.some(
        (child) =>
          child.mergedFrom &&
          child.mergedFrom.getCreatedAt().compare(id.getCreatedAt()) === 0,
      );

    for (const child of splitNode.allChildren) {
      const mergedFrom = child.mergedFrom;
      if (!mergedFrom) {
        continue;
      }
      // The product carries its own `mergedFrom` copied off the node it split
      // (`cloneElement`); a child stamped for that same element was merged in
      // one level up and is not what this split re-created.
      if (
        splitNode.mergedFrom &&
        mergedFrom
          .getCreatedAt()
          .compare(splitNode.mergedFrom.getCreatedAt()) === 0
      ) {
        continue;
      }
      const source = this.findFloorNode(mergedFrom);
      if (
        source &&
        source.isRemoved &&
        source.id.getCreatedAt().compare(mergedFrom.getCreatedAt()) === 0 &&
        !stampedFor(splitTarget, source.id)
      ) {
        return source.id;
      }
    }

    return undefined;
  }

  /**
   * `mergedAnchorInterloperGuard` prepares the §9.4 per-node filter for a
   * style range whose end position was declared inside a parent that a
   * merge unknown to the styling client removed. The moved anchor child
   * resolves in the merge target, so the traversal covers nodes sitting
   * between the merge-source tombstone and the moved children — nodes the
   * styling client saw outside its range, after the then-live parent. The
   * predicate skips exactly those interlopers.
   */
  private mergedAnchorInterloperGuard(
    pos: CRDTTreePos,
    versionVector?: VersionVector,
  ):
    | {
        isInterloper: (node: CRDTTreeNode) => boolean;
        declaredParent: CRDTTreeNode;
        target: CRDTTreeNode;
      }
    | undefined {
    if (versionVector === undefined) {
      return;
    }
    const [declaredParent] = pos.toTreeNodePair(this);
    if (
      !declaredParent.isRemoved ||
      !declaredParent.mergedInto ||
      !declaredParent.removedAt ||
      ticketKnown(versionVector, declaredParent.removedAt)
    ) {
      return;
    }
    const target = this.resolveMergeTarget(declaredParent);
    // Restricted to the shape where the tombstone sits directly under the
    // merge target; in other shapes the resolved range already excludes
    // the interlopers.
    if (target === declaredParent || declaredParent.parent !== target) {
      return;
    }
    // Collect the target's children positioned after the merge-source
    // tombstone in one pass, so the predicate is O(depth) per node.
    const afterTombstone = new Set<CRDTTreeNode>();
    let seenTombstone = false;
    for (const child of target.allChildren) {
      if (child === declaredParent) {
        seenTombstone = true;
        continue;
      }
      if (seenTombstone) {
        afterTombstone.add(child);
      }
    }
    return {
      isInterloper: (node: CRDTTreeNode): boolean => {
        // Judge by the node's highest ancestor directly under the merge
        // target, so an interloper's descendants are skipped with it.
        let top = node;
        while (top.parent && top.parent !== target) {
          top = top.parent as CRDTTreeNode;
        }
        if (top.parent !== target) {
          return false;
        }
        // Fail open on any merge stamp: an earlier merge keeps the
        // ORIGINAL source in mergedFrom (first-move stamp rule), so
        // stamp equality cannot prove the node was outside the styled
        // range. Only stamp-free nodes are positively interlopers.
        if (top.mergedFrom) {
          return false;
        }
        return afterTombstone.has(top);
      },
      declaredParent,
      target,
    };
  }

  /**
   * `reversedFromAnchorRecovery` prepares the §9.4 from-side counterpart of
   * `mergedAnchorInterloperGuard` for a style range whose start position was
   * declared inside a parent that a merge unknown to the styling client
   * removed. The resolved range then collapses (start past end) and the
   * traversal misses nodes the styling client covered. The recovery
   * re-anchors the traversal start just after the last live sibling before
   * the merge-source tombstone; the caller must style only nodes the
   * returned predicate positively identifies as interlopers. Stamped nodes
   * in the span stay out of reach and fail open unstyled — see the §9.4
   * known limitations in yorkie's concurrent-merge-split design doc.
   */
  private reversedFromAnchorRecovery(
    from: CRDTTreePos,
    to: CRDTTreePos,
    fromParent: CRDTTreeNode,
    fromLeft: CRDTTreeNode,
    toParent: CRDTTreeNode,
    toLeft: CRDTTreeNode,
    versionVector?: VersionVector,
  ):
    | {
        fromParent: CRDTTreeNode;
        fromLeft: CRDTTreeNode;
        isInterloper: (node: CRDTTreeNode) => boolean;
      }
    | undefined {
    const guard = this.mergedAnchorInterloperGuard(from, versionVector);
    if (!guard) {
      return;
    }
    // Only a range that actually collapsed needs recovery: when both
    // anchors moved with the merge, the resolved range stays ordered and
    // still covers what the styling client covered. A range that resolved
    // empty counts as collapsed too -- the merge can leave the start anchor
    // exactly on the end one, and covering nothing loses the client's range
    // just as completely as covering it backwards. Unless the client's own
    // range was empty, in which case covering nothing is right.
    const fromIdx = this.toIndex(fromParent, fromLeft);
    const toIdx = this.toIndex(toParent, toLeft);
    if (fromIdx < toIdx || (fromIdx === toIdx && from.equals(to))) {
      return;
    }
    const { declaredParent, target } = guard;
    let anchorLeft: CRDTTreeNode = target;
    for (const child of target.allChildren) {
      if (child === declaredParent) {
        break;
      }
      if (!child.isRemoved) {
        anchorLeft = child;
      }
    }
    return {
      fromParent: target,
      fromLeft: anchorLeft,
      isInterloper: guard.isInterloper,
    };
  }

  /**
   * `styleSkipPredicate` builds the per-token skip checks shared by style
   * and removeStyle, as two predicates over the same state.
   *
   * `skipReached` answers "did the change reach this node at all": the
   * End-token unknown-split-sibling exclusion (§9.1), the §9.6 range-start
   * guard and the §9.4 merged-anchor interloper filter for the range-end
   * position. It is about the change, so it holds for every node a style
   * writes to, however that node was found.
   *
   * `skipToken` adds the one restriction that belongs to the index traversal
   * alone -- the §9.4 from-side recovery re-anchors the traversal start over
   * nodes the change never covered, so a recovered traversal may touch only
   * the interlopers the recovery positively identified. Nodes derived from
   * the change's own positions are not in that widened span and answer to
   * `skipReached` only.
   *
   * `beganInside` is the set of elements THIS replica's resolved range begins
   * inside -- the resolved from-parent and its ancestors, the only elements a
   * token traversal reaches through their End token alone.
   */
  private styleSkipPredicate(
    from: CRDTTreePos,
    to: CRDTTreePos,
    fromParent: CRDTTreeNode,
    versionVector?: VersionVector,
    recoveredInterloper?: (node: CRDTTreeNode) => boolean,
  ): {
    skipToken: (token: TreeToken<CRDTTreeNode>) => boolean;
    skipReached: (token: TreeToken<CRDTTreeNode>) => boolean;
    declaredTo: DeclaredLineage | undefined;
    declaredFrom: DeclaredLineage | undefined;
  } {
    const isVersionVectorEmpty = !versionVector || versionVector.size() === 0;
    const anchorGuard = this.mergedAnchorInterloperGuard(to, versionVector);
    // Resolved once and handed back to `styleTargets`: the lineage walks are
    // over peer-supplied pointers and depend only on the change's positions.
    const declaredTo = this.declaredLineage(to);
    const declaredFrom = this.declaredLineage(from);

    const beganInside = new Set<CRDTTreeNode>();
    for (
      let current: CRDTTreeNode | undefined = fromParent;
      current;
      current = current.parent as CRDTTreeNode | undefined
    ) {
      beganInside.add(current);
    }

    const skipReached = ([node, tokenType]: TreeToken<CRDTTreeNode>) => {
      // §9.1: skip styling via the End token when the node has an unknown
      // split sibling AND the change's range ended inside the node. Only
      // then is the End token in the range solely because a concurrent
      // split extended the range into the sibling. When the range ran past
      // the node's end the End token was in it before any split existed.
      if (
        tokenType === TokenType.End &&
        !isVersionVectorEmpty &&
        (!declaredTo || declaredTo.covers(node)) &&
        this.hasUnknownSplitSibling(node, versionVector!)
      ) {
        return true;
      }
      // §9.6, the mirror of §9.1 on the range-start side. This replica's
      // range begins inside the node, so only its End token is in the
      // range; the change reached it that way only if the change's own
      // range-start position was declared inside it. A merge that removes a
      // paragraph's opening tag moves its children, and the start anchor
      // with them, into an element the change never entered.
      if (
        tokenType === TokenType.End &&
        !isVersionVectorEmpty &&
        beganInside.has(node) &&
        declaredFrom &&
        !declaredFrom.coversEitherWay(node)
      ) {
        return true;
      }
      // §9.4: the node is in the range only because an unknown merge
      // pulled the range-end anchor past it.
      return !!anchorGuard && anchorGuard.isInterloper(node);
    };

    const skipToken = (token: TreeToken<CRDTTreeNode>) => {
      if (skipReached(token)) {
        return true;
      }
      // §9.4 from-side: a recovered traversal may only touch nodes the
      // collapsed range lost, the positively identified interlopers.
      return !!recoveredInterloper && !recoveredInterloper(token[0]);
    };

    return { skipToken, skipReached, declaredTo, declaredFrom };
  }

  /**
   * `beginsAtOrBefore` reports whether the change's declared range-start sits
   * at or before `node`'s Start token in document order, both measured with
   * removed nodes included so a concurrent removal between the two moves
   * neither. A position that does not resolve answers no, as Go's
   * `beginsAtOrInside` does.
   */
  private beginsAtOrBefore(node: CRDTTreeNode, from: CRDTTreePos): boolean {
    if (!node.parent) {
      return false;
    }
    try {
      const [parent, left] = from.toTreeNodePair(this);
      // A concurrent merge can move the left sibling out of the declared
      // parent. Go's FindOffset fails there; findOffset here returns -1 and
      // toTreePos would resolve offset 0, so the case is refused explicitly.
      if (left !== parent && left.parent !== parent) {
        return false;
      }
      const offset = node.parent.findOffset(node, true);
      const nodeIdx = this.indexTree.indexOf(
        { node: node.parent, offset },
        true,
      );
      const declaredIdx = this.toIndex(parent, left, true);
      return declaredIdx >= 0 && declaredIdx <= nodeIdx;
    } catch {
      return false;
    }
  }

  /**
   * `declaredParentOf` returns the element a position named as its parent,
   * or undefined when this replica cannot resolve the position.
   */
  private declaredParentOf(pos: CRDTTreePos): CRDTTreeNode | undefined {
    const parent = this.findFloorNode(pos.getParentID());
    if (!parent || !this.findFloorNode(pos.getLeftSiblingID())) {
      return;
    }
    return parent;
  }

  /**
   * `splitOriginsOf` walks back along `insPrevID` from `node`, feeding each
   * element it passes to `visit` and stopping when `visit` returns false.
   * Only `splitElement` sets `insPrevID` on an element, so the chain is
   * exactly the split lineage; the walker stops a crafted chain that loops
   * back on itself, and a text link cuts it.
   */
  private splitOriginsOf(
    node: CRDTTreeNode,
    visit: (origin: CRDTTreeNode) => boolean,
  ): void {
    const walker = new InsNextWalker();
    let current: CRDTTreeNode | undefined = node;
    walker.visit(current);
    while (current && visit(current)) {
      if (!current.insPrevID) {
        return;
      }
      const prev = this.findFloorNode(current.insPrevID);
      if (!prev || prev.isText || !walker.visit(prev)) {
        return;
      }
      current = prev;
    }
  }

  /**
   * `declaredLineageOf` resolves, once for a change, everything
   * `DeclaredLineage` answers about the element a position declared as its
   * parent: the element's current ancestry, and the split lineage of each
   * ancestor.
   */
  private declaredLineageOf(declaredParent: CRDTTreeNode): DeclaredLineage {
    // Every element a declared ancestor was split from. `endsInside` asked
    // this one ancestor at a time; the union answers all of them at once, and
    // a chain already walked needs no second walk because the chain back from
    // an element is fixed.
    const ancestors = new Set<CRDTTreeNode>();
    const products = new Set<CRDTTreeNode>();
    for (
      let ancestor: CRDTTreeNode | undefined = declaredParent;
      ancestor;
      ancestor = ancestor.parent as CRDTTreeNode | undefined
    ) {
      ancestors.add(ancestor);
      this.splitOriginsOf(ancestor, (origin) => {
        if (products.has(origin)) {
          return false;
        }
        products.add(origin);

        return true;
      });
    }

    // The opposite direction -- a declared ancestor is a piece split off the
    // node being asked about -- cannot be precomputed, since it starts from
    // that node. Memoize it instead: the answer for a node is the answer for
    // every node whose chain runs through it, so one walk settles the whole
    // prefix it passed.
    const reversed = new Map<CRDTTreeNode, boolean>();
    const reaches = (node: CRDTTreeNode): boolean => {
      const cached = reversed.get(node);
      if (cached !== undefined) {
        return cached;
      }
      const walked: Array<CRDTTreeNode> = [];
      let found = false;
      this.splitOriginsOf(node, (origin) => {
        if (ancestors.has(origin)) {
          found = true;

          return false;
        }
        const memo = reversed.get(origin);
        if (memo !== undefined) {
          found = memo;

          return false;
        }
        walked.push(origin);

        return true;
      });
      for (const visited of walked) {
        reversed.set(visited, found);
      }

      return found;
    };

    return {
      covers: (node: CRDTTreeNode): boolean => products.has(node),
      coversEitherWay: (node: CRDTTreeNode): boolean =>
        products.has(node) || reaches(node),
    };
  }

  /**
   * `declaredAncestryHas` walks the CURRENT ancestry of the element a
   * position named as its parent, up to the root, and reports whether any of
   * them matches.
   */
  private declaredAncestryHas(
    declaredParent: CRDTTreeNode,
    match: (ancestor: CRDTTreeNode) => boolean,
  ): boolean {
    for (
      let current: CRDTTreeNode | undefined = declaredParent;
      current;
      current = current.parent as CRDTTreeNode | undefined
    ) {
      if (match(current)) {
        return true;
      }
    }
    return false;
  }

  /**
   * `declaredLineage` is `declaredLineageOf` for the element `pos` named as
   * its parent, or undefined when this replica cannot resolve the position.
   *
   * `covers` on the result is "the change's range ended inside this node":
   * whether the node is the element the position named, or an ancestor of it.
   * The ancestry is the current one, and a concurrent split moves the children
   * after the split point into the new right half, so each ancestor is matched
   * through its split lineage: every product of splitting the node stands for
   * the node.
   *
   * `coversEitherWay` is the same question for the range-start position
   * (§9.6), matching in BOTH directions along the split lineage: a split of
   * the element the range began inside leaves the start anchor in whichever
   * half holds it, and both halves are the element the change began inside.
   * For the range-end side the extra matches would make a guard that excludes
   * nodes fail open, so that side uses `covers`.
   */
  private declaredLineage(pos: CRDTTreePos): DeclaredLineage | undefined {
    const declaredParent = this.declaredParentOf(pos);

    return declaredParent ? this.declaredLineageOf(declaredParent) : undefined;
  }

  /**
   * `unknownSplitSiblings` returns the elements a split concurrent with the
   * change produced from `node`: its `insNextID` chain, stopped at the first
   * sibling the change already knew about. A style that covered `node`
   * covered these too, because the change resolved its range while `node`
   * was still one piece.
   */
  private unknownSplitSiblings(
    node: CRDTTreeNode,
    versionVector?: VersionVector,
  ): Array<CRDTTreeNode> {
    const siblings: Array<CRDTTreeNode> = [];
    const walker = new InsNextWalker();
    let current = node;
    walker.visit(current);
    while (current.insNextID) {
      const next = this.findFloorNode(current.insNextID);
      if (!next || next.isText) {
        break;
      }
      // Stop on a chain that loops back on itself; see InsNextWalker.
      if (!walker.visit(next)) {
        break;
      }
      if (ticketKnown(versionVector, next.id.getCreatedAt())) {
        break;
      }
      siblings.push(next);
      current = next;
    }
    return siblings;
  }

  /**
   * `splitFamilyOf` walks back along `insPrevID` from an element whose
   * creation the change did not know, collecting the products a concurrent
   * split made of a node it did know. It returns the family in document
   * order, that known node first, or an empty array when the chain reaches
   * no such node -- a node simply new to the change.
   */
  private splitFamilyOf(
    node: CRDTTreeNode,
    versionVector?: VersionVector,
  ): Array<CRDTTreeNode> {
    const reversed = [node];
    const walker = new InsNextWalker();
    let current = node;
    walker.visit(current);
    while (current.insPrevID) {
      const prev = this.findFloorNode(current.insPrevID);
      if (!prev || prev.isText || !walker.visit(prev)) {
        return [];
      }
      reversed.push(prev);
      if (prev.canStyle(versionVector)) {
        return reversed.reverse();
      }
      current = prev;
    }
    return [];
  }

  /**
   * `boundaryRangeCovers` reports whether the change declared a range that
   * covers at least one element, so that reconstructing its boundary
   * elements is repair rather than invention. A range whose two positions
   * are equal covers nothing on any replica. A range that resolves
   * backwards covers nothing here either; only a removal collapses a range,
   * so an inversion with a live declared ancestry on both ends is the
   * caller's own and gets nothing.
   */
  private boundaryRangeCovers(
    from: CRDTTreePos,
    to: CRDTTreePos,
    fromParent: CRDTTreeNode,
    fromLeft: CRDTTreeNode,
    toParent: CRDTTreeNode,
    toLeft: CRDTTreeNode,
  ): boolean {
    if (from.equals(to)) {
      return false;
    }
    if (this.toIndex(fromParent, fromLeft) <= this.toIndex(toParent, toLeft)) {
      return true;
    }
    return (
      this.declaredAncestryRemoved(from) || this.declaredAncestryRemoved(to)
    );
  }

  /**
   * `declaredAncestryRemoved` reports whether the element a position named
   * as its parent, or any ancestor of it, has been removed -- the only way a
   * range declared forwards can resolve backwards here.
   */
  private declaredAncestryRemoved(pos: CRDTTreePos): boolean {
    const parent = this.declaredParentOf(pos);
    return !!parent && this.declaredAncestryHas(parent, (n) => n.isRemoved);
  }

  /**
   * `boundaryElements` returns the elements the change reached through a
   * single token on the replica that issued it: the ancestors of the
   * range-start position, whose End tokens the range ran past, and the
   * ancestors of the range-end position, whose Start tokens it ran past --
   * each chain stopping below the common ancestor of the two. Each element is
   * returned with the token the range ran past, so the caller can put it
   * through the same per-token skip checks the traversal applies.
   *
   * These come from the positions the change carries, which never move, so
   * every replica computes the same set whatever a concurrent merge did to
   * the index space.
   */
  private boundaryElements(
    from: CRDTTreePos,
    to: CRDTTreePos,
  ): Array<TreeToken<CRDTTreeNode>> {
    const fromParent = this.declaredParentOf(from);
    const toParent = this.declaredParentOf(to);
    if (!fromParent || !toParent) {
      return [];
    }

    const fromAncestors = new Set<CRDTTreeNode>();
    for (
      let current: CRDTTreeNode | undefined = fromParent;
      current;
      current = current.parent as CRDTTreeNode | undefined
    ) {
      fromAncestors.add(current);
    }

    // The common ancestor is the first ancestor of the range-end position
    // that also sits above the range-start position.
    let common: CRDTTreeNode | undefined;
    const startTokens: Array<TreeToken<CRDTTreeNode>> = [];
    for (
      let current: CRDTTreeNode | undefined = toParent;
      current;
      current = current.parent as CRDTTreeNode | undefined
    ) {
      if (fromAncestors.has(current)) {
        common = current;
        break;
      }
      startTokens.push([current, TokenType.Start]);
    }
    if (!common) {
      return [];
    }

    const elements: Array<TreeToken<CRDTTreeNode>> = [];
    for (
      let current: CRDTTreeNode | undefined = fromParent;
      current && current !== common;
      current = current.parent as CRDTTreeNode | undefined
    ) {
      elements.push([current, TokenType.End]);
    }
    return elements.concat(startTokens);
  }

  /**
   * `styleTargets` resolves the nodes a style or removeStyle applies to,
   * without repeats, along with the size the boundary text splits added to
   * live. Traversed nodes come in document order; boundary elements
   * recovered from the change's positions follow them, as in Go.
   *
   * The two operations are one range resolution asked to write two
   * different things, so the resolution lives here once. A node is reported
   * at most once even though a fully covered element is visited on both its
   * Start and End tokens; the second write was already a no-op, since RHT
   * rejects a ticket that is not after the one it holds.
   *
   * NOTE(cross-implementation): the set this returns is a replicated
   * contract -- every SDK and the server have to reach the same nodes for
   * the same change. It mirrors yorkie's `styleTargets`; the rules are the
   * "Port specification" in yorkie's docs/design/concurrent-merge-split.md.
   */
  private styleTargets(
    from: CRDTTreePos,
    to: CRDTTreePos,
    editedAt: TimeTicket,
    versionVector?: VersionVector,
  ): [Array<CRDTTreeNode>, DataSize] {
    const diff = { data: 0, meta: 0 };
    const [[resolvedFromParent, fromLeftRaw], diffFrom] =
      this.findNodesAndSplitText(from, editedAt, 'range');
    const [[toParent, toLeftRaw], diffTo] = this.findNodesAndSplitText(
      to,
      editedAt,
      'range',
    );
    addDataSizes(diff, diffTo, diffFrom);

    // skipActorID for the same reason as edit's Phase 2: a same-boundary
    // empty run has to resolve here the way the split loop resolves it.
    const styleActorID = editedAt.getActorID();
    let fromParent = resolvedFromParent;
    let fromLeft =
      fromLeftRaw !== fromParent
        ? this.advancePastUnknownSplitSiblings(
            fromLeftRaw,
            versionVector,
            false,
            styleActorID,
          )
        : fromLeftRaw;
    const toLeft =
      toLeftRaw !== toParent
        ? this.advancePastUnknownSplitSiblings(
            toLeftRaw,
            versionVector,
            false,
            styleActorID,
          )
        : toLeftRaw;

    const isVersionVectorEmpty = !versionVector || versionVector.size() === 0;
    const recovery = this.reversedFromAnchorRecovery(
      from,
      to,
      fromParent,
      fromLeft,
      toParent,
      toLeft,
      versionVector,
    );
    if (recovery) {
      fromParent = recovery.fromParent;
      fromLeft = recovery.fromLeft;
    }
    const { skipToken, skipReached, declaredTo, declaredFrom } =
      this.styleSkipPredicate(
        from,
        to,
        fromParent,
        versionVector,
        recovery?.isInterloper,
      );

    const targets: Array<CRDTTreeNode> = [];
    const seen = new Set<CRDTTreeNode>();
    const add = (node: CRDTTreeNode) => {
      if (!seen.has(node)) {
        seen.add(node);
        targets.push(node);
      }
    };

    this.traverseInPosRange(fromParent, fromLeft, toParent, toLeft, (token) => {
      const [node, tokenType] = token;
      // A fully covered element arrives twice, on its Start token and on
      // its End token. Everything below depends only on the node, so the
      // second visit would redo the lineage walks for nothing.
      if (seen.has(node) || skipToken(token)) {
        return;
      }

      if (node.canStyle(versionVector)) {
        add(node);
        // Carry the style onto split products the change could not have
        // known, so a range resolved before the split still covers the piece
        // the split cut off. Which token reached the node does not decide
        // this: a node the range covered whole arrives on its Start token,
        // or on its End token alone when the range began inside it.
        for (const sibling of this.unknownSplitSiblings(node, versionVector)) {
          add(sibling);
        }
        return;
      }

      // The change did not know this node was created, so it cannot have
      // named it -- unless a concurrent split produced it from one the
      // change did name. An End token the range genuinely ran past says the
      // change styled that element while it was still one piece, so the
      // whole split family carries the style, including the half the change
      // knew and the traversal no longer reaches.
      if (
        tokenType === TokenType.End &&
        !isVersionVectorEmpty &&
        declaredTo &&
        !declaredTo.covers(node)
      ) {
        const family = this.splitFamilyOf(node, versionVector);
        // The family is reached only through the node the change knew, which
        // is the one the End-token guard judges. When that guard excludes
        // it, re-adding the family would style the very node it skipped.
        //
        // The change must also have begun at or inside that node. A split of
        // more than one level carries the right half into a new parent, past
        // a range that began right after the known node, and its End token
        // then enters the range with nothing to do with the change. A range
        // that began BEFORE the known node covered it whole, however the
        // traversal lost it, and keeps the closure. This is rule 2(c) of the
        // Port specification in yorkie's concurrent-merge-split design doc
        // (§9.2 Fix 27, yorkie#2070).
        if (
          family.length &&
          !skipToken([family[0], TokenType.End]) &&
          declaredFrom &&
          (declaredFrom.coversEitherWay(family[0]) ||
            this.beginsAtOrBefore(family[0], from))
        ) {
          family.forEach(add);
        }
      }
    });

    // Add back the boundary elements the change itself reached. Without a
    // concurrent merge the traversal has already found every one of them;
    // with one, they are the nodes the resolved range can no longer see.
    if (
      this.boundaryRangeCovers(from, to, fromParent, fromLeft, toParent, toLeft)
    ) {
      for (const token of this.boundaryElements(from, to)) {
        // The §9.4 interloper filters and the End-token split guard are
        // about which nodes the change reached, not about how the traversal
        // found them, so a boundary element answers to them too.
        if (!skipReached(token) && token[0].canStyle(versionVector)) {
          add(token[0]);
        }
      }
    }

    return [targets, diff];
  }

  /**
   * `orderSameBoundarySplit` decides which node a split at `offset` of
   * `parent` actually splits, so that concurrent splits of one node at one
   * boundary land in the same order on every replica.
   *
   * `splitElement` places its product directly after the node it splits.
   * When a concurrent split of the same boundary has already been applied,
   * that puts the products in arrival order, which differs per replica. The
   * XML still matches (all but the last product are empty) but position-based
   * operations that follow do not.
   *
   * Order them by ticket instead, newest first, as RGA orders concurrent
   * inserts after the same node: skip the unknown split siblings with a newer
   * ticket and split the last of them at its start. The right half lives in
   * that sibling on this replica, so it moves into our product exactly as it
   * would have moved out of `parent` on a replica that applied us first.
   */
  private orderSameBoundarySplit(
    parent: CRDTTreeNode,
    offset: number,
    editedAt: TimeTicket,
    versionVector?: VersionVector,
  ): [CRDTTreeNode, number] {
    // A concurrent split of the same boundary took everything to the right of
    // it, so only a split at the end of `parent` can be one.
    if (!versionVector || offset !== parent.allChildren.length) {
      return [parent, offset];
    }

    let target = parent;
    const walker = new InsNextWalker();
    walker.visit(target);
    while (target.insNextID) {
      const next = this.findFloorNode(target.insNextID);
      if (!next || next.isText || !next.parent) {
        break;
      }

      // Stop on a chain that loops back on itself; see InsNextWalker.
      if (!walker.visit(next)) {
        break;
      }

      // The sibling has to belong to target's own split family. The strict
      // parent-equality check §7.5 uses is too strong here — at a
      // multi-level split the sibling may already sit under the next level's
      // product — but dropping it entirely would let an insNextID that did
      // not come from `splitElement` redirect this split onto an arbitrary
      // element elsewhere in the tree.
      if (!this.sharesSplitFamilyParent(target, next)) {
        break;
      }

      // Splitting a tombstoned sibling would make our product born
      // tombstoned, which a replica that applied us before the concurrent
      // split never does. Fall back to splitting parent, as that replica
      // did, rather than diverge on liveness.
      if (next.isRemoved) {
        break;
      }

      const createdAt = next.id.getCreatedAt();
      if (createdAt.getActorID() === editedAt.getActorID()) {
        break;
      }
      const knownLamport = versionVector.get(createdAt.getActorID());
      if (
        knownLamport !== undefined &&
        knownLamport >= createdAt.getLamport()
      ) {
        break;
      }
      if (!createdAt.after(editedAt)) {
        break;
      }

      target = next;
    }

    return target === parent ? [parent, offset] : [target, 0];
  }

  /**
   * `sharesSplitFamilyParent` reports whether `next` sits under `node`'s
   * parent, or under a split product of that parent. A multi-level split
   * moves a sibling under the next level's product, so the two parents
   * legitimately differ — but only within one split family. Anything beyond
   * that is not a split sibling of `node`, whatever its insNextID claims.
   */
  private sharesSplitFamilyParent(
    node: CRDTTreeNode,
    next: CRDTTreeNode,
  ): boolean {
    if (!node.parent || !next.parent) {
      return false;
    }
    if (node.parent === next.parent) {
      return true;
    }

    const walker = new InsNextWalker();
    let current: CRDTTreeNode | undefined = node.parent as CRDTTreeNode;
    while (current && walker.visit(current)) {
      if (current === next.parent) {
        return true;
      }
      if (!current.insNextID) {
        return false;
      }
      current = this.findFloorNode(current.insNextID);
    }

    return false;
  }

  /**
   * `advancePastUnknownSplitSiblings` follows the insNextID chain of the
   * given node, advancing past element-type split siblings that the editing
   * client did not know about (not in versionVector).
   */
  private advancePastUnknownSplitSiblings(
    node: CRDTTreeNode,
    versionVector?: VersionVector,
    relaxParentCheck = false,
    skipActorID?: string,
  ): CRDTTreeNode {
    if (!versionVector || !node) {
      return node;
    }

    let current = node;
    const walker = new InsNextWalker();
    walker.visit(current);
    while (current.insNextID) {
      const next = this.findFloorNode(current.insNextID);
      if (!next || next.isText) {
        break;
      }

      // Stop on a chain that loops back on itself; see InsNextWalker.
      if (!walker.visit(next)) {
        break;
      }

      // §7.5: Skip parent check when relaxParentCheck is true — at
      // ancestor iterations of the split loop, a concurrent recursive
      // split may have moved the sibling to a different parent.
      if (!relaxParentCheck && next.parent !== current.parent) {
        break;
      }

      const actorID = next.id.getCreatedAt().getActorID();

      // §7.7: Stop at siblings created by the current operation's
      // actor. They are our own split products, not concurrent ones.
      if (skipActorID !== undefined && actorID === skipActorID) {
        break;
      }

      const knownLamport = versionVector.get(actorID);
      if (
        knownLamport !== undefined &&
        knownLamport >= next.id.getCreatedAt().getLamport()
      ) {
        break;
      }

      // Empty unknown siblings standing right before our own product are
      // concurrent splits of the same boundary, ordered ahead of ours by
      // `orderSameBoundarySplit`. They are not content the editor meant to
      // keep on its left; a replica that applied them later re-parented them
      // after the boundary (§7.4), so stay in front of them here as well.
      if (
        skipActorID !== undefined &&
        this.emptyRunReachesActor(next, skipActorID, versionVector)
      ) {
        break;
      }

      current = next;
    }

    return current;
  }

  /**
   * `emptyRunReachesActor` reports whether the insNextID chain starting at
   * `node` runs through empty, unknown element split siblings only and then
   * reaches a node created by `actorID`.
   */
  private emptyRunReachesActor(
    node: CRDTTreeNode,
    actorID: string,
    versionVector: VersionVector,
  ): boolean {
    const walker = new InsNextWalker();
    let current: CRDTTreeNode | undefined = node;
    while (current && !current.isText && walker.visit(current)) {
      const createdAt = current.id.getCreatedAt();
      if (createdAt.getActorID() === actorID) {
        return current !== node;
      }
      const knownLamport = versionVector.get(createdAt.getActorID());
      if (
        current.allChildren.length > 0 ||
        (knownLamport !== undefined && knownLamport >= createdAt.getLamport())
      ) {
        return false;
      }
      current = current.insNextID
        ? this.findFloorNode(current.insNextID)
        : undefined;
    }
    return false;
  }

  /**
   * `hasUnknownSplitSibling` checks whether the given element node has a
   * split sibling (via insNextID) whose creation the editor did not know
   * about. Used to prevent styling via End tokens when a concurrent split
   * extended the range into the split sibling.
   */
  private hasUnknownSplitSibling(
    node: CRDTTreeNode,
    versionVector: VersionVector,
  ): boolean {
    if (!node.insNextID) {
      return false;
    }

    const next = this.findFloorNode(node.insNextID);
    if (!next || next.isText) {
      return false;
    }

    // NOTE: Unlike advancePastUnknownSplitSiblings, we intentionally omit
    // the parent-equality check. In multi-level splits (splitLevel>=2),
    // the split sibling may have been moved to a different parent by the
    // recursive ancestor split. The End-token guard must still fire because
    // the node WAS split — insNextID is only set by SplitElement.
    const actorID = next.id.getCreatedAt().getActorID();
    const knownLamport = versionVector.get(actorID);

    return (
      knownLamport === undefined ||
      knownLamport < next.id.getCreatedAt().getLamport()
    );
  }

  /**
   * `registerNode` registers the given node to the tree, keeping a live node
   * over a tombstone when both claim the same ID.
   *
   * Documents written by older clients can carry two nodes under one ID (an
   * undo that re-inserted a deleted piece by copy). A plain `put` lets the
   * winner depend on the order the nodes were registered — operation order on
   * a live document, document order on one rebuilt from a snapshot — so after
   * a reload the same position resolves to a different node and its offset can
   * fall outside that node. Keeping the live one makes both orders agree for a
   * live/tombstone pair, which is the shape those documents carry.
   *
   * Two nodes in the same state keep the last-registered-wins behavior, and
   * stay order-dependent: element IDs issued for a split can legitimately
   * collide with an inserted node's ID (see the delimiter note in
   * `TreeEditOperation`), and that resolution order is what the rest of the
   * tree already assumes.
   *
   * A node this refuses stays in the index tree while another node answers for
   * its ID, so it is reachable by traversal but not by lookup. That is the
   * intended trade for a duplicate: the alternative is unregistering a node
   * that positions still resolve through.
   */
  public registerNode(node: CRDTTreeNode): void {
    const entry = this.nodeMapByID.floorEntry(node.id);
    if (
      entry &&
      entry.value !== node &&
      entry.key.equals(node.id) &&
      node.isRemoved &&
      !entry.value.isRemoved
    ) {
      return;
    }

    this.nodeMapByID.put(node.id, node);
  }

  /**
   * `dropDuplicateContents` returns the contents that would not put a second
   * node under an ID already in the tree.
   *
   * Content created by an edit carries that edit's lamport and actor, so a
   * content node whose ID names another change is a copy of a node that
   * already exists — what the copy-reinsert undo path sends when it reverses a
   * deletion. Inserting it would leave two nodes under one identity, so the
   * copy is dropped and the rest of the edit applies.
   *
   * Content from this edit's own change is kept even when its ID collides: the
   * delimiters an element split consumes are simulated rather than replayed,
   * so an ID issued here can legitimately collide, and dropping it would lose
   * a node the client already inserted.
   *
   * Dropping rather than failing is deliberate: such changes are already in
   * the history of existing documents, and a change that cannot be replayed is
   * a document that can never be loaded again. A collision anywhere in a
   * content node's subtree drops that whole subtree, on the grounds that a
   * copy is copied whole.
   */
  public dropDuplicateContents(
    contents: Array<CRDTTreeNode>,
    editedAt: TimeTicket,
  ): Array<CRDTTreeNode> {
    return contents.filter((content) => {
      let reused = false;
      traverseAll(content, (node) => {
        const createdAt = node.id.getCreatedAt();
        if (
          createdAt.getLamport() === editedAt.getLamport() &&
          createdAt.getActorID() === editedAt.getActorID()
        ) {
          return;
        }

        const entry = this.nodeMapByID.floorEntry(node.id);
        if (entry && entry.key.equals(node.id)) {
          reused = true;
        }
      });

      return !reused;
    });
  }

  /**
   * `registerPendingGCPair` buffers a GC pair for a node that was born
   * tombstoned (split off an already-removed node). The pair is picked up
   * by the next `edit` or `style` call via `drainPendingGCPairs`. `size`
   * is the net-new size created by the split; it is accounted to
   * docSize.gc at registration since the node was never live.
   */
  public registerPendingGCPair(node: CRDTTreeNode, size: DataSize): void {
    this.pendingGCPairs.push({ parent: this, child: node, gcOnlySize: size });
  }

  /**
   * `pushPendingGCPair` buffers an already-built pair, for garbage whose
   * parent is not this tree -- an attribute tombstone belongs to the node
   * holding it, not to the tree.
   */
  public pushPendingGCPair(pair: GCPair): void {
    this.pendingGCPairs.push(pair);
  }

  /**
   * `drainPendingGCPairs` returns the buffered GC pairs and clears the
   * buffer.
   */
  public drainPendingGCPairs(): Array<GCPair> {
    const pairs = this.pendingGCPairs;
    this.pendingGCPairs = [];
    return pairs;
  }

  /**
   * `findNodesAndSplitText` finds `TreePos` of the given `CRDTTreeNodeID` and
   * splits nodes if the position is in the middle of a text node.
   *
   * The ids of the given `pos` are the ids of the node in the CRDT perspective.
   * This is different from `TreePos` which is a position of the tree in the
   * physical perspective.
   *
   * If `editedAt` is given, then it is used to find the appropriate left node
   * for concurrent insertion.
   *
   * `boundary` selects how a position inside a merged-away parent resolves:
   * `insert` places it at the insertion boundary in the merge target (before
   * the first moved child, so RGA ordering breaks ties), while `range` places
   * it right after the merge-source tombstone so a style range neither grows
   * over nor shrinks past nodes concurrently inserted at that anchor.
   */
  public findNodesAndSplitText(
    pos: CRDTTreePos,
    editedAt?: TimeTicket,
    boundary: 'insert' | 'range' = 'insert',
  ): [TreeNodePair, DataSize] {
    const diff = { data: 0, meta: 0 };

    // 01. Find the parent and left sibling node of the given position.
    const [parent, leftSibling] = pos.toTreeNodePair(this);
    let leftNode = leftSibling;

    // 02. Determine whether the position is left-most and the exact parent
    // in the current tree.
    const isLeftMost = parent === leftNode;
    const realParent =
      leftNode.parent && !isLeftMost ? leftNode.parent : parent;

    // 02-1. If the parent has been tombstoned by a merge, redirect to the
    // merge destination using the forwarding pointer. The insertion
    // boundary is the first child in the target whose `mergedFrom`
    // points back at the tombstoned parent (i.e. the first child moved
    // by the merge, in target child order).
    if (realParent.isRemoved && isLeftMost && realParent.mergedInto) {
      // §9.3 Range Boundary at Merged-Away Anchors: a range boundary
      // resolves to the position right after the merge-source tombstone,
      // not the insertion boundary below. The insertion boundary sits
      // before the first moved child, so it would extend a style range
      // over nodes concurrently inserted between the tombstone and the
      // moved children — nodes the styling client saw outside its range
      // (after the then-live parent).
      if (boundary === 'range' && realParent.parent) {
        return [[realParent.parent as CRDTTreeNode, realParent], diff];
      }
      // `findMergeNode`, not `findFloorNode`: a text node is never a merge
      // destination, and neither is a node the pointer merely floors onto.
      // The pointer can come from a client -- `mergedInto` is derived from
      // the `mergedFrom` an element payload keeps -- and returned as the
      // insertion parent it would fail every later edit that resolves
      // through this tombstone, on every replica. Falling through to the
      // normal path treats the crafted lineage as the absent one it is.
      const mergeTarget = this.findMergeNode(realParent.mergedInto);
      if (mergeTarget && !mergeTarget.isRemoved) {
        const allCh = mergeTarget.allChildren;
        for (let i = 0; i < allCh.length; i++) {
          const targetChild = allCh[i];
          if (
            !targetChild.mergedFrom ||
            !targetChild.mergedFrom.equals(realParent.id)
          ) {
            continue;
          }
          if (i === 0) {
            return [[mergeTarget, mergeTarget], diff];
          }
          return [[mergeTarget, allCh[i - 1]], diff];
        }
        // Fallback: insert at leftmost of merge target.
        return [[mergeTarget, mergeTarget], diff];
      }
    }

    // 03. Split text node if the left node is a text node.
    if (leftNode.isText) {
      const [, splitedDiff] = leftNode.split(
        this,
        pos.getLeftSiblingID().getOffset() - leftNode.id.getOffset(),
      );
      // Accumulated, not assigned: `diff` is provably empty here today, but
      // assigning is the same trap that left the split phase unaccounted.
      addDataSizes(diff, splitedDiff);
    }

    // 04. Find the appropriate left node. If some nodes are inserted at the
    // same position concurrently, then we need to find the appropriate left
    // node. This is similar to RGA.
    if (editedAt) {
      const allChildren = realParent.allChildren;
      const index = isLeftMost ? 0 : allChildren.indexOf(leftNode) + 1;

      for (let i = index; i < allChildren.length; i++) {
        const next = allChildren[i];
        if (!next.id.getCreatedAt().after(editedAt)) {
          break;
        }

        leftNode = next;
      }
    }

    return [[realParent, leftNode], diff];
  }

  /**
   * `style` applies the given attributes of the given range.
   */
  public style(
    range: [CRDTTreePos, CRDTTreePos],
    attributes: { [key: string]: string } | undefined,
    editedAt: TimeTicket,
    versionVector?: VersionVector,
  ): [
    Array<GCPair>,
    Array<TreeChange>,
    DocSize,
    Map<string, string>,
    Array<string>,
  ] {
    const size = { live: { data: 0, meta: 0 }, gc: { data: 0, meta: 0 } };
    const [targets, diff] = this.styleTargets(
      range[0],
      range[1],
      editedAt,
      versionVector,
    );
    addDataSizes(size.live, diff);

    const changes: Array<TreeChange> = [];
    const pairs: Array<GCPair> = [];
    const prevAttributes = new Map<string, string>();
    const newAttrKeys: Array<string> = [];
    if (attributes) {
      const attrs: { [key: string]: any } = parseObjectValues(attributes);
      targets.forEach((node, i) => {
        // Capture previous attribute values from the first styled node for
        // the reverse operation (undo).
        if (i === 0) {
          for (const key of Object.keys(attributes)) {
            if (node.attrs?.has(key)) {
              prevAttributes.set(key, node.attrs.get(key)!);
            } else {
              newAttrKeys.push(key);
            }
          }
        }

        const writes = node.setAttrs(attributes, editedAt);
        const affectedAttrs: { [key: string]: string } = {};
        for (const write of writes) {
          if (write.installed) {
            const key = write.installed.getKey();
            affectedAttrs[key] = attrs[key];
          }
          accAttrWrite(write, node, !node.isRemoved, pairs, size);
        }

        // A tombstoned node is not part of the rendered document, and
        // `toIndex` on one yields a zero-width range that means nothing to
        // an editor. The text half makes the same exclusion.
        if (Object.keys(affectedAttrs).length > 0 && !node.isRemoved) {
          const range = this.styleChangeRange(node);
          if (range) {
            changes.push({
              type: TreeChangeType.Style,
              ...range,
              actor: editedAt.getActorID(),
              value: affectedAttrs,
            });
          }
        }
      });
    }

    pairs.push(...this.drainPendingGCPairs());

    return [pairs, changes, size, prevAttributes, newAttrKeys];
  }

  /**
   * `removeStyle` removes the given attributes of the given range.
   */
  public removeStyle(
    range: [CRDTTreePos, CRDTTreePos],
    attributesToRemove: Array<string>,
    editedAt: TimeTicket,
    versionVector?: VersionVector,
  ): [Array<GCPair>, Array<TreeChange>, DocSize, Map<string, string>] {
    const size = { live: { data: 0, meta: 0 }, gc: { data: 0, meta: 0 } };
    const [targets, diff] = this.styleTargets(
      range[0],
      range[1],
      editedAt,
      versionVector,
    );
    addDataSizes(size.live, diff);

    const changes: Array<TreeChange> = [];
    const pairs: Array<GCPair> = [];
    const prevAttributes = new Map<string, string>();
    if (attributesToRemove) {
      targets.forEach((node, i) => {
        if (!node.attrs) {
          node.attrs = new RHT();
        }

        // Capture previous attribute values from the first styled node for
        // the reverse operation (undo).
        if (i === 0) {
          for (const key of attributesToRemove) {
            if (node.attrs.has(key)) {
              prevAttributes.set(key, node.attrs.get(key)!);
            }
          }
        }

        // `canStyle` admits a node removed concurrently with this change,
        // so `nodeIsLive` is the third question `attrGCPair` asks.
        const nodeIsLive = !node.isRemoved;
        let removedAny = false;
        for (const key of attributesToRemove) {
          let wasLive = node.attrs.has(key);
          const { gcNodes, valueDropped } = node.attrs.remove(key, editedAt);
          removedAny = removedAny || gcNodes.length > 0;

          // The tombstone is not charged for its value; those bytes leave
          // whichever side was holding them. See `attrGCPair` for the split.
          subDataSize(nodeIsLive ? size.live : size.gc, valueDropped);

          for (const rhtNode of gcNodes) {
            pairs.push(attrGCPair(node, rhtNode, wasLive, nodeIsLive));
            // Only the node replacing the live value takes a size out of
            // live; a second one in the same call is the tombstone it
            // superseded.
            wasLive = false;
          }
        }

        // See `style`: a tombstoned node reports no change to editors, and
        // neither does a node that held none of the attributes -- the
        // `style` half reports only what it actually wrote, so a removal
        // that took nothing off is the same no-op and stays silent.
        if (nodeIsLive && removedAny) {
          const range = this.styleChangeRange(node);
          if (range) {
            changes.push({
              type: TreeChangeType.RemoveStyle,
              ...range,
              actor: editedAt.getActorID(),
              value: attributesToRemove,
            });
          }
        }
      });
    }

    pairs.push(...this.drainPendingGCPairs());

    return [pairs, changes, size, prevAttributes];
  }

  /**
   * `styleChangeRange` returns the index and path range a style change on
   * the given node reports to editors, or undefined when the node is not in
   * the document.
   *
   * The targets come from walks over ids -- split families, merge lineage --
   * so one can be a node that is live by `isRemoved` but detached from the
   * tree, with no parent. `prevSibling` dereferences `parent!` and would
   * throw on it; a node with no parent has no place in the rendered
   * document to report either, so the caller reports nothing.
   */
  private styleChangeRange(node: CRDTTreeNode) {
    const parent = node.parent;
    if (!parent) {
      return undefined;
    }

    const previous = node.prevSibling || parent;
    return {
      from: this.toIndex(parent, previous),
      to: this.toIndex(node, node),
      fromPath: this.toPath(parent, previous),
      toPath: this.toPath(node, node),
    };
  }

  /**
   * `edit` edits the tree with the given range and content.
   * If the content is undefined, the range will be removed.
   */
  public edit(
    range: [CRDTTreePos, CRDTTreePos],
    contents: Array<CRDTTreeNode> | undefined,
    splitLevel: number,
    editedAt: TimeTicket,
    issueTimeTicket: (() => TimeTicket) | undefined,
    versionVector?: VersionVector,
  ): [
    Array<TreeChange>,
    Array<GCPair>,
    DataSize,
    Array<CRDTTreeNode>,
    number,
    number,
    Set<string>,
    Array<TreeRestoreSpan>,
    Array<TreeRestoreSpan>,
    number,
    number,
    Array<CRDTTreeNode>,
    Array<[CRDTTreeNodeID, CRDTTreeNodeID]>,
  ] {
    const diff = { data: 0, meta: 0 };

    // 01. find nodes from the given range and split nodes.
    const [[fromParent, fromLeftRaw], diffFrom] = this.findNodesAndSplitText(
      range[0],
      editedAt,
    );
    const [[toParent, toLeftRaw], diffTo] = this.findNodesAndSplitText(
      range[1],
      editedAt,
    );

    addDataSizes(diff, diffTo, diffFrom);

    // 01-1. Advance past split siblings unknown to the editing client.
    // When a concurrent SplitElement created siblings linked via insNextID,
    // the editor's position was computed against the unsplit tree. Advance
    // past siblings the editor could not have seen so that the range
    // starts/ends after all concurrent split products.
    // Skip when leftNode == parent (leftmost child position).
    // §7.7: pass the editing actor so a same-boundary empty run resolves
    // here exactly as the split loop resolves it; without it the two sides
    // would place the same boundary differently.
    const editActorID = editedAt.getActorID();
    const fromLeft =
      fromLeftRaw !== fromParent
        ? this.advancePastUnknownSplitSiblings(
            fromLeftRaw,
            versionVector,
            false,
            editActorID,
          )
        : fromLeftRaw;
    const toLeft =
      toLeftRaw !== toParent
        ? this.advancePastUnknownSplitSiblings(
            toLeftRaw,
            versionVector,
            false,
            editActorID,
          )
        : toLeftRaw;

    // Phase 3: Range Narrowing — when fromLeft and toLeft are in
    // different parents (due to a concurrent element split), follow
    // fromLeft's insNextID chain to find a split sibling in toParent
    // and narrow the traversal range. The original fromParent/fromLeft
    // are preserved for merge, split, and insert steps.
    // VV-independent for clone/root consistency.
    let collectFromParent: CRDTTreeNode = fromParent;
    let collectFromLeft: CRDTTreeNode = fromLeft;
    if (fromLeft !== fromParent && fromParent !== toParent) {
      let current: CRDTTreeNode = fromLeft;
      const walker = new InsNextWalker();
      walker.visit(current);
      while (current.insNextID) {
        const next = this.findFloorNode(current.insNextID);
        if (!next || next.isText) {
          break;
        }
        // Stop on a chain that loops back on itself; see InsNextWalker.
        if (!walker.visit(next)) {
          break;
        }
        if (next.parent && next.parent === toParent) {
          // Skip narrowing when toLeft === toParent (leftmost child
          // position, offset 0). The narrowed collectFromLeft would be
          // a child at offset >= 1, producing a backwards range that
          // suppresses the intended merge.
          if (toLeft !== toParent) {
            collectFromLeft = next;
            collectFromParent = toParent;
          }
          break;
        }
        current = next;
      }
    }

    const fromIdx = this.toIndex(fromParent, fromLeft);
    const fromPath = this.toPath(fromParent, fromLeft);

    const nodesToBeRemoved: Array<CRDTTreeNode> = [];
    const tokensToBeRemoved: Array<TreeToken<CRDTTreeNode>> = [];
    const toBeMovedToFromParents: Array<CRDTTreeNode> = [];
    const toBeMergedNodes: Array<CRDTTreeNode> = [];
    const preTombstoned = new Set<string>();

    this.traverseInPosRange(
      collectFromParent,
      collectFromLeft,
      toParent,
      toLeft,
      ([node, tokenType], ended) => {
        // NOTE(hackerwins): If the node overlaps as a start tag with the
        // range then we need to move the remaining children to fromParent.
        if (tokenType === TokenType.Start && !ended) {
          // Fix 9: Skip merge for elements created by concurrent
          // operations. The editor didn't know about this element,
          // so crossing into it is an artifact of a concurrent split,
          // not an intentional merge.
          if (ticketKnown(versionVector, node.id.getCreatedAt())) {
            toBeMergedNodes.push(node);
            // Include removed children (allChildren) so tombstones move
            // with the merge and survive as RGA anchors; a concurrent
            // insert referencing one then resolves in the merge target
            // and orders via the RGA tie-break.
            for (const child of node.allChildren) {
              toBeMovedToFromParents.push(child);
            }
          }
        }

        // NOTE(sigmaith): Determine if the node's creation event was visible.
        const creationKnown = ticketKnown(
          versionVector,
          node.id.getCreatedAt(),
        );

        // NOTE(sigmaith): Determine if existing tombstone was already causally known.
        const tombstoneKnown =
          !!node.removedAt && ticketKnown(versionVector, node.removedAt);

        // NOTE(sejongk): If the node is removable or its parent is going to
        // be removed, then this node should be removed.
        // Do not cascade-delete children of merge-boundary nodes
        // (toBeMergedNodes), because those children are moved rather than
        // deleted.
        if (
          node.canDelete(editedAt, creationKnown, tombstoneKnown) ||
          (nodesToBeRemoved.includes(node.parent!) &&
            !toBeMergedNodes.includes(node.parent!))
        ) {
          // NOTE(hackerwins): If the node overlaps as an end token with the
          // range then we need to keep the node.
          if (tokenType === TokenType.Text || tokenType === TokenType.Start) {
            // Track nodes already tombstoned before this edit so the
            // reverse operation does not accidentally resurrect them.
            if (node.isRemoved) {
              preTombstoned.add(node.id.toIDString());
            }
            nodesToBeRemoved.push(node);

            // Cascade delete to split siblings created by concurrent
            // SplitElement. Only for element nodes.
            if (
              !node.isText &&
              node.insNextID &&
              !toBeMergedNodes.includes(node)
            ) {
              const walker = new InsNextWalker();
              walker.visit(node);
              let next = this.findFloorNode(node.insNextID);
              // Stop on a chain that loops back on itself; see
              // InsNextWalker. Unbounded here would also grow
              // nodesToBeRemoved without limit.
              while (next && walker.visit(next)) {
                if (!ticketKnown(versionVector, next.id.getCreatedAt())) {
                  nodesToBeRemoved.push(next);
                  // Cascade through the full subtree, not just immediate children.
                  traverseAll(next, (n) => {
                    if (n !== next) {
                      nodesToBeRemoved.push(n);
                    }
                  });
                }
                if (!next.insNextID) break;
                next = this.findFloorNode(next.insNextID);
              }
            }
          }
          tokensToBeRemoved.push([node, tokenType]);
        }
      },
      true,
    );

    // NOTE(hackerwins): If concurrent deletion happens, we need to separate the
    // range(from, to) into multiple ranges.
    const changes: Array<TreeChange> = this.makeDeletionChanges(
      tokensToBeRemoved,
      editedAt,
    );

    // 01-2. Count merged nodes before children are moved (step 03).
    // The undo system uses this count to generate a split reverse op
    // instead of re-inserting empty shells.
    const mergeLevel = toBeMergedNodes.length;
    // Track how many GC pairs exist right after the plain-delete loop; if the
    // merge phases (steps 03/03-1) add more, this edit involved merge-child
    // propagation and its captured spans are NOT a complete description of the
    // deletion — the op layer then falls back to the copy-reinsert reverse.
    let deletePairCount = 0;

    // 02. Delete: delete the nodes that are marked as removed.
    const pairs: Array<GCPair> = [];
    // Attribute tombstones ridden in on the inserted content. Held aside and
    // appended only after `spansComplete` is decided: that test reads
    // `pairs.length` as "did anything beyond the plain deletes produce
    // garbage", and these say nothing about whether the captured spans
    // describe the deletion.
    const contentAttrPairs: Array<GCPair> = [];
    const removedSpans: Array<TreeRestoreSpan> = [];
    // Captured in the insert phase: identity spans of the nodes this edit
    // inserts, so an undo re-removes them by identity (not by index, which
    // would clobber concurrently-restored content) and a redo revives them.
    const insertedSpans: Array<TreeRestoreSpan> = [];
    for (const node of nodesToBeRemoved) {
      if (node.remove(editedAt)) {
        pairs.push({ parent: this, child: node });
        // Identity-preserving undo: capture one span per node THIS edit
        // transitioned visible → tombstoned. `remove()` returning true is
        // exactly that transition, so pre-tombstoned nodes and LWW overwrites
        // are excluded automatically. `nodesToBeRemoved` is in traversal
        // order → parents precede children, which `restore()` relies on when
        // recreating purged subtrees.
        const parent = node.parent as CRDTTreeNode | undefined;
        let leftSiblingID: CRDTTreeNodeID | undefined;
        let rightSiblingID: CRDTTreeNodeID | undefined;
        if (parent) {
          const siblings = parent.allChildren;
          const idx = siblings.indexOf(node);
          if (idx > 0) {
            leftSiblingID = this.leftAnchorID(siblings[idx - 1]);
          }
          if (idx >= 0 && idx < siblings.length - 1) {
            rightSiblingID = siblings[idx + 1].id;
          }
        }
        removedSpans.push({
          id: node.id,
          nodeType: node.type,
          isText: node.isText,
          length: node.isText ? node.value.length : 0,
          value: node.isText ? node.value : undefined,
          attrs: node.attrs?.deepcopy(),
          parentID: parent?.id,
          leftSiblingID,
          rightSiblingID,
        });
      }
    }
    deletePairCount = pairs.length;

    // 03. Merge: move the nodes that are marked as moved. Only
    // `mergedFrom` and `mergedAt` are written on the moved child —
    // both are persisted in the snapshot encoding. `mergedAt` must be
    // captured explicitly here (not read from source.removedAt at use
    // time) because the source's `removedAt` is mutated by LWW when a
    // later concurrent tombstone targets the same node.
    //
    // §6.3 Chained-Merge Flattening (Fix 20): a merge chain P->Q->R is
    // kept flat so runtime state matches what `rebuildMergeState` derives
    // from a snapshot (which can only ever represent the compressed chain,
    // because it records one `mergedFrom` pointer per child and reads the
    // child's final physical parent). The destination is resolved through
    // `resolveMergeTarget`, so children merged into an already-merged-away
    // parent forward to the final live target instead of piling up under
    // the removed intermediate.
    const dest = this.resolveMergeTarget(fromParent);
    for (const node of toBeMovedToFromParents) {
      // A moved child must have a source parent to record; skip otherwise
      // rather than append an untracked node (a node without `mergedFrom`
      // is invisible to the merge-delete propagation and rebuild logic).
      if (!node.parent) {
        continue;
      }
      // Tombstoned children are moved too (kept removed): they stay as RGA
      // anchors so a concurrent insert referencing one resolves in the
      // merge target and orders via the RGA tie-break, converging with the
      // replica that inserted before the merge. `moveChild` keeps the size
      // accounting correct for both live and tombstoned children
      // (visible-neutral for the latter), so index positions stay correct.
      //
      // `mergedFrom` and `mergedAt` are stamped together, only on the
      // first move, so a child carried through a chained merge keeps its
      // original source P and the original P->Q merge ticket (Fix 20).
      // Stamping `mergedAt` on every move would diverge: a replica that
      // applied P->Q then Q->R would record the Q->R ticket, while a
      // replica where Q was already merged records the P->Q ticket on the
      // single forwarded move.
      if (!node.mergedFrom) {
        node.mergedFrom = node.parent.id;
        node.mergedAt = editedAt;
      }
      dest.moveChild(node);
      // Point this child's original source at the resolved destination,
      // path-compressing a transitive source (a prior merge whose children
      // were just relocated again) from the now-removed intermediate to the
      // final target. This runtime cache is rebuilt from `mergedFrom` on
      // snapshot load. Deriving `mergedInto` from a *moved* child — one that
      // had a parent above — mirrors `rebuildMergeState`, which likewise
      // skips parentless children, so runtime and snapshot agree. (A
      // parentless child, detached by a concurrent split cascade, is
      // `continue`d above and must not repoint its source here.)
      //
      // `mergedInto` is set solely from moved children (never from the
      // merge-source list directly), so it is set only when
      // `rebuildMergeState` can reconstruct it — a source with no moved child
      // of its own (e.g. an intermediate that only relayed another source's
      // children) is left unset on both paths, keeping runtime and snapshot
      // consistent.
      //
      // Resolved through `findMergeNode` for the same reason
      // `rebuildMergeState` resolves it there: `mergedFrom` is stamped here
      // only when the node carries none, so a node that arrived on an element
      // payload keeps the client's value, and this is where that value is
      // read again long after the decode that first saw it.
      //
      // The source must also already be a tombstone, the same requirement
      // `rebuildMergeState` enforces: a boundary element is removed in step 02
      // above before its children move here, so a live element named by a
      // client-supplied `mergedFrom` is not a source this merge created.
      // Checking it on only one of the two paths would make a loaded snapshot
      // disagree with the replica that applied the op.
      const src = this.findMergeNode(node.mergedFrom);
      if (src && src.removedAt) {
        src.mergedInto = dest.id;
      }
    }

    // 03-1. Propagate deletes to children moved by prior merges.
    // When a merge-source node is fully deleted (not a merge boundary),
    // its former children in the merge target should also be deleted.
    // Compare against the resolved `dest`, not `fromParent`: the forwarding
    // pointers above point at the flattened target (§6.3), so a chained
    // merge (dest !== fromParent) must recognize a concurrent-merge boundary
    // by `dest`. The list of moved children is recomputed on the fly from
    // the merge target's children filtered by `mergedFrom`.
    //
    // The boundaries the edit's own positions name are resolved lazily: §1.1
    // redirects a position away from a merged-away parent, so fromParent
    // and toParent no longer say which boundaries the edit asked for.
    let declared: Set<CRDTTreeNode> | undefined;
    for (const node of nodesToBeRemoved) {
      if (!node.mergedInto || toBeMergedNodes.includes(node)) {
        continue;
      }
      // A source whose children already sit in this edit's own destination
      // stands for a merge the edit itself asks for only when one of the
      // edit's positions named that source: the range then stops at the
      // source instead of covering it, and keeping the children is what
      // makes the two replicas agree (§6.2). A source the range merely spans
      // is a plain delete of everything that was inside it, so its children
      // are tombstoned wherever the concurrent merge left them.
      if (node.mergedInto.equals(dest.id)) {
        declared ??= this.declaredBoundaries(range[0], range[1]);
        if (declared.has(node)) {
          continue;
        }
      }
      // The destination has to be the node `mergedInto` names exactly, and an
      // element: this loop tombstones that node's children, so a floor lookup
      // landing on a neighbour is the cascade reaching live nodes no merge
      // ever moved.
      const mergeTarget = this.findMergeNode(node.mergedInto);
      if (!mergeTarget) {
        continue;
      }
      for (const targetChild of mergeTarget.allChildren) {
        if (
          !targetChild.mergedFrom ||
          !targetChild.mergedFrom.equals(node.id)
        ) {
          continue;
        }
        if (targetChild.removedAt) {
          continue;
        }
        if (targetChild.remove(editedAt)) {
          pairs.push({ parent: this, child: targetChild });
        }
        // Also tombstone descendants if the moved child is an element.
        traverseAll(targetChild, (n) => {
          if (n !== targetChild && !n.removedAt) {
            if (n.remove(editedAt)) {
              pairs.push({ parent: this, child: n });
            }
          }
        });
      }
    }

    // 04. Split: split the element nodes for the given split level.
    //
    // The boundaries it opens grow the visible index, which nothing else in
    // this method reports: they are not content, so `insertedContentSize`
    // below does not see them, and they remove nothing, so the removed range
    // does not either. Measured off the tree rather than computed as
    // 2 * splitLevel, so a split whose product is born tombstoned, or one the
    // tree has no room for, reports the growth it really produced.
    const sizeBeforeSplit = this.getSize();
    // The `[removed element, its replacement]` pairs this split produced —
    // see `mergeSourceOf`. Reported so a replica applying the split, its own
    // or a peer's, can re-point whatever it recorded against the removed one.
    const splitRecreatedIDs: Array<[CRDTTreeNodeID, CRDTTreeNodeID]> = [];
    if (splitLevel > 0) {
      let splitCount = 0;
      let parent = fromParent;
      let left: CRDTTreeNode = fromLeft;
      while (splitCount < splitLevel) {
        // §7.5 Per-Iteration Advance: advance past unknown element
        // split siblings at the current ancestor level. skipActorID
        // (§7.7) prevents advancing past own split products.
        if (left !== parent) {
          left = this.advancePastUnknownSplitSiblings(
            left,
            versionVector,
            true,
            editedAt.getActorID(),
          );
          // If the advance moved left to a node under a different
          // parent, update parent to match.
          if (left.parent && left.parent !== parent) {
            parent = left.parent as CRDTTreeNode;
          }
        }

        if (!parent.parent) {
          break;
        }

        // The metadata the new element adds belongs in docSize.live, the
        // same as every other `split` caller books it. Dropping it here left
        // live without the elements a split mints, so a split and the merge
        // that undoes it did not cancel out and the live size walked down by
        // a ticket per cycle, without bound.
        const [target, offset] = this.orderSameBoundarySplit(
          parent,
          left !== parent ? parent.findOffset(left, true) + 1 : 0,
          editedAt,
          versionVector,
        );
        const [splitNode, splitDiff] = target.split(
          this,
          offset,
          issueTimeTicket,
          versionVector,
        );
        addDataSizes(diff, splitDiff);
        const recreated = splitNode && this.mergeSourceOf(splitNode, target);
        if (recreated) {
          splitRecreatedIDs.push([recreated, splitNode!.id]);
        }

        left = parent;
        parent = parent.parent! as CRDTTreeNode;
        splitCount++;
      }
      changes.push({
        type: TreeChangeType.Content,
        from: fromIdx,
        to: fromIdx,
        fromPath,
        toPath: fromPath,
        actor: editedAt.getActorID(),
      });
    }
    const splitSize = this.getSize() - sizeBeforeSplit;

    // 05. Insert: insert the given nodes at the given position.
    //
    // The identity check runs here rather than on entry: resolving the range
    // above splits text nodes, and a split can create the very ID a content
    // node carries. Checking before that would let the copy through and leave
    // two nodes under one ID. `insertedContentSize` is measured now, while the
    // content is still detached — inserting under a removed parent tombstones
    // it and shrinks what its size reads back as.
    if (contents) {
      contents = this.dropDuplicateContents(contents, editedAt);
    }
    const insertedContentSize = contents
      ? contents.reduce((sum, node) => sum + node.paddedSize(), 0)
      : 0;

    if (contents?.length) {
      // §9.4: When the insert position was declared inside a parent that a
      // concurrent merge removed, the content physically lands in the merge
      // target. Stamp it as merged-from the declared parent so it stays
      // distinguishable from nodes that were never inside that parent —
      // style-range resolution and merge-delete propagation key on this.
      const [declaredFromParent] = range[0].toTreeNodePair(this);
      const intendedParent =
        declaredFromParent !== fromParent &&
        declaredFromParent.isRemoved &&
        declaredFromParent.mergedInto &&
        this.resolveMergeTarget(declaredFromParent) === fromParent
          ? declaredFromParent
          : undefined;
      let intendedMergedAt: TimeTicket | undefined;
      if (intendedParent) {
        // Take the merge ticket from a sibling the merge moved; the parent's
        // own removedAt may have been overwritten by a later LWW tombstone.
        for (const child of fromParent.allChildren) {
          if (child.mergedFrom?.equals(intendedParent.id) && child.mergedAt) {
            intendedMergedAt = child.mergedAt;
            break;
          }
        }
        intendedMergedAt ??= intendedParent.removedAt;
      }

      const aliveContents: Array<CRDTTreeNode> = [];
      let leftInChildren = fromLeft; // tree
      for (const content of contents) {
        // 05-1. Insert the content nodes to the tree.
        if (leftInChildren === fromParent) {
          // 05-1-1. when there's no leftSibling, then insert content into very front of parent's children.
          fromParent.insertAt(content, 0);
        } else {
          // 05-1-2. insert after leftSibling
          fromParent.insertAfter(content, leftInChildren);
        }

        if (intendedParent) {
          content.mergedFrom = intendedParent.id;
          content.mergedAt = intendedMergedAt;
        }

        leftInChildren = content;
        traverseAll(content, (node) => {
          // If insertion happens during concurrent editing and parent node has been removed,
          // make new nodes as tombstone immediately.
          if (fromParent.isRemoved) {
            node.remove(editedAt);

            // The node is born dead: this branch adds nothing to `diff`, so
            // its bytes never entered docSize.live. A pair without
            // `gcOnlySize` would debit live for them anyway, driving live
            // below the tree's real size until purge. Register the
            // post-`remove` size (tombstone ticket included) so gc is
            // charged exactly what `purge` later gives back, and leave live
            // untouched -- the same routing the attribute tombstones a few
            // lines below use.
            pairs.push({
              parent: this,
              child: node,
              gcOnlySize: node.getDataSize(),
            });
          } else {
            addDataSizes(diff, node.getDataSize());
          }

          this.registerNode(node);

          // NOTE: Content can arrive carrying attribute tombstones. The undo
          // copy-reinsert path legitimately produces them -- it re-sends a
          // `deepcopy` of nodes a `removeStyle` tombstoned, tombstones
          // included, so the reinserted node keeps rejecting the stale styles
          // the original rejects -- and a crafted payload can invent them,
          // since `fromRHT` decodes `isRemoved` verbatim. Either way they are
          // garbage this insert created in this tree: no removal path ran for
          // them here, so without this nothing would ever collect them.
          //
          // `getDataSize` skips removed attributes, so these bytes never
          // entered live and `gcOnlySize` (inside `CRDTTreeNode.getGCPairs`)
          // sends them straight to docSize.gc, which purge gives back. This is
          // the same routing a split (`splitElement`), a snapshot load and a
          // Set/Add element payload (`CRDTTree.getGCPairs`) already use; the
          // pair map keys on the parent's identity, so a clone wearing the
          // original's RHTNode ids is a distinct pair, not a cancellation.
          for (const attrPair of node.getGCPairs()) {
            contentAttrPairs.push(attrPair);
          }

          // Capture this inserted node's identity span (parent-before-child
          // via traverseAll) for identity-preserving insert undo/redo.
          const p = node.parent as CRDTTreeNode | undefined;
          let leftSiblingID: CRDTTreeNodeID | undefined;
          let rightSiblingID: CRDTTreeNodeID | undefined;
          if (p) {
            const sibs = p.allChildren;
            const idx = sibs.indexOf(node);
            if (idx > 0) leftSiblingID = this.leftAnchorID(sibs[idx - 1]);
            if (idx >= 0 && idx < sibs.length - 1) {
              rightSiblingID = sibs[idx + 1].id;
            }
          }
          insertedSpans.push({
            id: node.id,
            nodeType: node.type,
            isText: node.isText,
            length: node.isText ? node.value.length : 0,
            value: node.isText ? node.value : undefined,
            attrs: node.attrs?.deepcopy(),
            parentID: p?.id,
            leftSiblingID,
            rightSiblingID,
          });
        });

        if (!content.isRemoved) {
          aliveContents.push(content);
        }
      }
      if (aliveContents.length) {
        const value = aliveContents.map((content) => toTreeNode(content));
        if (changes.length && changes[changes.length - 1].from === fromIdx) {
          changes[changes.length - 1].value = value;
        } else {
          changes.push({
            type: TreeChangeType.Content,
            from: fromIdx,
            to: fromIdx,
            fromPath,
            toPath: fromPath,
            actor: editedAt.getActorID(),
            value,
          });
        }
      }
    }

    pairs.push(...this.drainPendingGCPairs());

    // Identity-preserving restore only covers plain deletions. If this edit
    // merged nodes or its merge propagation removed extra nodes, the captured
    // spans don't fully describe the deletion → signal the op layer (empty
    // spans) to keep the copy-reinsert reverse.
    const spansComplete = mergeLevel === 0 && pairs.length === deletePairCount;
    pairs.push(...contentAttrPairs);

    return [
      changes,
      pairs,
      diff,
      nodesToBeRemoved,
      fromIdx,
      mergeLevel,
      preTombstoned,
      spansComplete ? removedSpans : [],
      // `traverseAll` is post-order (children before parent), so reverse to get
      // parent-before-child — the order `restore()` needs to recreate a purged
      // subtree top-down (a child's recreate resolves its parent by identity).
      spansComplete ? insertedSpans.reverse() : [],
      insertedContentSize,
      splitSize,
      // The merge-boundary elements this edit removed — the ones whose
      // children it moved into the merge target, and so the ones a split
      // reversing it has to re-create. A strict subset of `nodesToBeRemoved`,
      // which also holds whole elements deleted inside the range, their
      // cascade-deleted descendants and nodes already tombstoned.
      toBeMergedNodes,
      splitRecreatedIDs,
    ];
  }

  /**
   * `editT` edits the given range with the given value.
   * This method uses indexes instead of a pair of TreePos for testing.
   */
  public editT(
    range: [number, number],
    contents: Array<CRDTTreeNode> | undefined,
    splitLevel: number,
    editedAt: TimeTicket,
    issueTimeTicket: () => TimeTicket,
  ): [
    Array<TreeChange>,
    Array<GCPair>,
    DataSize,
    Array<CRDTTreeNode>,
    number,
    number,
    Set<string>,
    Array<TreeRestoreSpan>,
    Array<TreeRestoreSpan>,
    number,
    number,
    Array<CRDTTreeNode>,
    Array<[CRDTTreeNodeID, CRDTTreeNodeID]>,
  ] {
    const fromPos = this.findPos(range[0]);
    const toPos = this.findPos(range[1]);
    return this.edit(
      [fromPos, toPos],
      contents,
      splitLevel,
      editedAt,
      issueTimeTicket,
    );
  }

  /**
   * `move` move the given source range to the given target range.
   */
  public move(
    target: [number, number],
    source: [number, number],
    ticket: TimeTicket,
  ): void {
    // TODO(hackerwins, easylogic): Implement this with keeping references of the nodes.
    throw new YorkieError(
      Code.ErrUnimplemented,
      `not implemented: ${target}, ${source}, ${ticket}`,
    );
  }

  /**
   * `pathToTreePos` converts the given path of the node to the TreePos.
   */
  public pathToTreePos(
    path: Array<number>,
  ): ReturnType<typeof this.indexTree.pathToTreePos> {
    return this.indexTree.pathToTreePos(path);
  }

  /**
   * `purgeBarrierAt` implements `GCParent.purgeBarrierAt`. `findNodesAndSplitText` walks the
   * parent's children, removed ones included, advancing while the next sibling
   * was created after the incoming edit; a tombstoned sibling with an older
   * ticket ends that walk. Purging detaches it from the parent, so the next
   * sibling inherits the decision and must be causally stable first.
   */
  public purgeBarrierAt(node: GCChild): TimeTicket | undefined {
    if (!(node instanceof CRDTTreeNode) || !node.parent) {
      return;
    }

    // Read the children in place: `allChildren` copies, and this runs once
    // per stable tombstone on every collection pass.
    const siblings = node.parent._children;
    const offset = siblings.indexOf(node);
    if (offset < 0) {
      return;
    }
    return siblings[offset + 1]?.id.getCreatedAt();
  }

  /**
   * `purge` physically purges the given node.
   */
  public purge(node: CRDTTreeNode): void {
    node.parent?.removeChild(node);
    // `nodeMapByID` is keyed by ID, so an unconditional remove would also
    // unregister a different node that shares this one's ID — see
    // `registerNode`. Only drop the entry this node actually holds.
    const entry = this.nodeMapByID.floorEntry(node.id);
    if (entry && entry.value === node && entry.key.equals(node.id)) {
      this.nodeMapByID.remove(node.id);
    }

    const insPrevID = node.insPrevID;
    const insNextID = node.insNextID;

    // NOTE: findFloorNode may return undefined when the insertion neighbor was
    // already purged (restore/recreate reorders GC so a neighbor can be
    // collected first). Guard both derefs rather than assuming survival.
    if (insPrevID) {
      const insPrev = this.findFloorNode(insPrevID);
      if (insPrev) {
        insPrev.insNextID = insNextID;
      }
    }

    if (insNextID) {
      const insNext = this.findFloorNode(insNextID);
      if (insNext) {
        insNext.insPrevID = insPrevID;
      }
    }

    node.insPrevID = undefined;
    node.insNextID = undefined;
  }

  /**
   * `dissolveMerge` erases the merge lineage a node about to be revived
   * leaves behind: the forwarding pointer on the source and the
   * `mergedFrom`/`mergedAt` stamps on the children that merge moved into the
   * destination.
   *
   * `mergedInto` is derivable only for a source that is already a tombstone —
   * `rebuildMergeState` and the merge in `edit` both require it — so a revived
   * source that kept either half of the lineage makes the replica running the
   * undo disagree with one loading a snapshot: keeping `mergedInto` gives the
   * undoing replica a pointer the snapshot no longer rebuilds, and keeping the
   * children's `mergedFrom` lets the snapshot rebuild one the moment a redo
   * (`retombstone`) tombstones the source again. Clearing both leaves nothing
   * either path can derive, in either direction.
   *
   * The destination is resolved through `findMergeNode` for the same reason
   * the §6.2 cascade does: `mergedInto` is derived from a `mergedFrom` an
   * element payload may carry, so only an exact element match names a node the
   * pointer really meant. The source's own pointer is dropped regardless of
   * whether that lookup succeeds — it is the half that is never persisted.
   */
  private dissolveMerge(src: CRDTTreeNode): void {
    if (!src.mergedInto) {
      return;
    }
    const dest = this.findMergeNode(src.mergedInto);
    src.mergedInto = undefined;
    if (!dest) {
      return;
    }

    for (const child of dest.allChildren) {
      if (child.mergedFrom?.equals(src.id)) {
        child.mergedFrom = undefined;
        child.mergedAt = undefined;
      }
    }
  }

  /**
   * `restore` re-establishes the nodes described by `spans` under their
   * ORIGINAL identities (identity-preserving Tree undo): live → skip
   * (idempotent), tombstoned → unremove in place, purged → recreate. Spans
   * must be in parent-before-child order (`edit()` captures them that way).
   *
   * Returns `[untombstoned, recreated, pairs, diff]`:
   * - `untombstoned`: nodes revived in place (caller unregisters their GC pairs);
   * - `recreated`: brand-new nodes rebuilt for purged ranges (caller adds their
   *   size to Live);
   * - `pairs`: pending GC pairs for born-removed remainders split off a removed
   *   straddler (caller registers them BEFORE unregistering the untombstoned);
   * - `diff`: the metadata overhead of splitting live straddlers (caller `acc`s
   *   it to Live);
   * - `edits`: one insertion per node that became visible, in the order it
   *   did. Each is measured right after its node comes back, so applying them
   *   one after another reproduces the result.
   */
  public restore(
    spans: Array<TreeRestoreSpan>,
    editedAt: TimeTicket,
  ): [
    Array<CRDTTreeNode>,
    Array<CRDTTreeNode>,
    Array<GCPair>,
    DataSize,
    Array<TreeVisibleEdit>,
  ] {
    const untombstoned: Array<CRDTTreeNode> = [];
    const recreated: Array<CRDTTreeNode> = [];
    const diff: DataSize = { data: 0, meta: 0 };
    const changes: Array<TreeVisibleEdit> = [];
    const revived = (node: CRDTTreeNode) => {
      const change = this.makeInsertionChange(node, editedAt);
      if (change) {
        changes.push(change);
      }
    };

    for (const span of spans) {
      if (!span.isText) {
        const node = this.findFloorNode(span.id);
        if (node && node.id.equals(span.id)) {
          if (node.isRemoved) {
            this.dissolveMerge(node);
            node.unremove();
            untombstoned.push(node);
            revived(node);
          }
          continue;
        }
        const created = this.recreateFromSpan(
          span,
          span.id.getOffset(),
          span.length,
        );
        if (created) {
          recreated.push(created);
          revived(created);
        }
        continue;
      }

      // Text: pieces may be split finer than the span, and a concurrent op or
      // a post-GC recreate can leave pieces whose boundaries straddle it.
      // Isolate the exact [start, end) sub-range out of every overlapping
      // piece — splitting at the span boundaries, live or removed — so all
      // replicas converge on identical text-node segmentation (the tree
      // analogue of RGATreeSplit.isolateRange). Then revive the removed parts
      // and recreate the purged gaps.
      const start = span.id.getOffset();
      const end = start + span.length;
      const pieces = this.findPiecesOverlapping(
        span.id.getCreatedAt(),
        start,
        end,
      );

      let cursor = start;
      let pieceIdx = 0;
      while (cursor < end) {
        const piece = pieces[pieceIdx];
        const pieceStart = piece ? piece.id.getOffset() : Infinity;
        const pieceEnd = piece ? pieceStart + piece.value.length : Infinity;

        if (piece && pieceStart <= cursor) {
          const overlapEnd = Math.min(pieceEnd, end);
          const target = this.isolateTextRange(piece, cursor, overlapEnd, diff);
          if (target.isRemoved) {
            target.unremove();
            untombstoned.push(target);
            revived(target);
          }
          cursor = overlapEnd;
          if (overlapEnd >= pieceEnd) pieceIdx++;
        } else {
          const gapEnd = Math.min(pieceStart, end);
          const created = this.recreateFromSpan(span, cursor, gapEnd - cursor);
          if (created) {
            recreated.push(created);
            revived(created);
          }
          cursor = gapEnd;
        }
      }
    }

    // Splitting a removed straddler buffers born-removed remainders as pending
    // GC pairs (see CRDTTreeNode.split). The caller registers these BEFORE
    // unregistering the un-tombstoned targets, so a target that was itself a
    // split-born piece is walked gc->live correctly (mirrors the Text path).
    const pairs = this.drainPendingGCPairs();
    return [untombstoned, recreated, pairs, diff, changes];
  }

  /**
   * `isolateTextRange` splits `piece` so that a node exactly covering the
   * absolute-offset interval [from, to) of its insertion exists, and returns
   * it. Splitting at the caller's boundaries — rather than skipping a piece
   * that straddles them — is what lets concurrent restores converge on the
   * same text-node segmentation across replicas (the tree analogue of
   * RGATreeSplit.isolateRange). A live split's metadata overhead is added to
   * `diff`; a removed split buffers a pending GC pair internally (contributing
   * zero here). Requires pieceStart <= from < to <= pieceEnd.
   */
  private isolateTextRange(
    piece: CRDTTreeNode,
    from: number,
    to: number,
    diff: DataSize,
  ): CRDTTreeNode {
    let node = piece;
    if (from > node.id.getOffset()) {
      const [right, splitDiff] = node.split(this, from - node.id.getOffset());
      addDataSizes(diff, splitDiff);
      node = right!;
    }
    if (to < node.id.getOffset() + node.value.length) {
      const [, splitDiff] = node.split(this, to - node.id.getOffset());
      addDataSizes(diff, splitDiff);
    }
    return node;
  }

  /**
   * `retombstone` re-deletes the nodes described by `spans` (redo of an
   * identity-preserving undo). Live pieces only; idempotent. A piece that
   * straddles a span boundary is split at that boundary so only the in-span
   * range is re-removed (symmetric with restore's isolate, so undo/redo stay
   * mirror images and segmentation stays convergent). Returns the GC pairs for
   * the newly tombstoned nodes, the live-split metadata overhead, and one
   * deletion per visible node it removed, each measured right before the
   * removal so they apply one after another.
   */
  public retombstone(
    spans: Array<TreeRestoreSpan>,
    executedAt: TimeTicket,
  ): [Array<GCPair>, DataSize, Array<TreeVisibleEdit>] {
    const pairs: Array<GCPair> = [];
    const diff: DataSize = { data: 0, meta: 0 };
    const changes: Array<TreeVisibleEdit> = [];
    for (const span of spans) {
      const start = span.id.getOffset();
      const end = start + Math.max(span.length, 1);
      const pieces = span.isText
        ? this.findPiecesOverlapping(span.id.getCreatedAt(), start, end)
        : [this.findFloorNode(span.id)].filter(
            (n): n is CRDTTreeNode => !!n && n.id.equals(span.id),
          );
      for (const piece of pieces) {
        if (piece.isRemoved) continue;
        let target = piece;
        if (piece.isText) {
          const from = Math.max(piece.id.getOffset(), start);
          const to = Math.min(piece.id.getOffset() + piece.value.length, end);
          target = this.isolateTextRange(piece, from, to, diff);
        }
        // Measure while `target` is still visible.
        const range = this.visibleRangeOf(target);
        if (target.remove(executedAt)) {
          pairs.push({ parent: this, child: target });
          if (range) {
            const [from, to, fromPath, toPath] = range;
            changes.push({
              change: {
                type: TreeChangeType.Content,
                from,
                to,
                fromPath,
                toPath,
                actor: executedAt.getActorID(),
              },
              insertedSize: 0,
            });
          }
        }
      }
    }
    return [pairs, diff, changes];
  }

  /**
   * `visibleRangeOf` returns the index range `node` covers and its paths, or
   * undefined when it or one of its ancestors is removed and so takes no room
   * in the index.
   */
  private visibleRangeOf(
    node: CRDTTreeNode,
  ): [number, number, Array<number>, Array<number>] | undefined {
    for (let n: CRDTTreeNode | undefined = node; n; n = n.parent) {
      if (n.isRemoved) {
        return;
      }
    }
    if (!node.parent) {
      return;
    }

    // `toIndex`/`indexToPath` throw on a tree they cannot walk (`invalid pos`,
    // `out of index range`). Both run here inside `TreeEditOperation.execute`,
    // which for a remote pack is driven from `Change.execute` off spans that
    // arrived from the wire: a throw there aborts the pack, the checkpoint
    // never advances, and the document is wedged for good on every replica
    // that receives it. Reporting the range is a courtesy to subscribers, not
    // part of convergence -- the CRDT state is already settled by the time we
    // measure -- so a tree we cannot measure degrades to "no position
    // reported", exactly as a node under a removed ancestor does above.
    try {
      const to = this.toIndex(node.parent, node);
      const from = to - node.paddedSize();
      return [from, to, this.indexToPath(from), this.indexToPath(to)];
    } catch (err) {
      logger.warn(`[TR] failed to measure restored node: ${err}`);
      return;
    }
  }

  /**
   * `makeInsertionChange` describes `node` becoming visible as an insertion
   * at its current position. A node still hidden under a removed ancestor
   * produces nothing.
   */
  private makeInsertionChange(
    node: CRDTTreeNode,
    editedAt: TimeTicket,
  ): TreeVisibleEdit | undefined {
    const range = this.visibleRangeOf(node);
    if (!range) {
      return;
    }

    const [from, to, path] = range;
    return {
      change: {
        type: TreeChangeType.Content,
        from,
        to: from,
        fromPath: path,
        toPath: path,
        actor: editedAt.getActorID(),
        value: [toTreeNode(node)],
      },
      // The change reports a collapsed range, as every insertion does, so the
      // size it added is not readable from it; reconciliation needs it.
      insertedSize: to - from,
    };
  }

  /**
   * `findPiecesOverlapping` collects surviving pieces (live or tombstoned) of
   * the text insertion `createdAt` overlapping [start, end), in ascending
   * offset order, via descending floorEntry probes.
   */
  private findPiecesOverlapping(
    createdAt: TimeTicket,
    start: number,
    end: number,
  ): Array<CRDTTreeNode> {
    const pieces: Array<CRDTTreeNode> = [];
    let probe = end - 1;
    while (probe >= 0) {
      const node = this.findFloorNode(CRDTTreeNodeID.of(createdAt, probe));
      if (!node || !node.isText) break;
      const nodeStart = node.id.getOffset();
      const nodeEnd = nodeStart + node.value.length;
      if (nodeEnd <= start) break;
      if (nodeStart < end && nodeEnd > start) pieces.push(node);
      if (nodeStart <= start) break;
      probe = nodeStart - 1;
    }
    return pieces.reverse();
  }

  /**
   * `recreateFromSpan` rebuilds a purged node (or purged text sub-range)
   * under its original identity and attaches it. Anchor ladder, each rung
   * doing floor-lookup + parent-identity check:
   *   (a) same-insertion successor/predecessor piece (text) → exact slot;
   *   (b) captured left boundary sibling still under this parent → after it;
   *   (c) captured right boundary sibling still under this parent → before it;
   *   (d) deterministic id-order fallback: insert among the parent's current
   *       children at the first position whose id compares greater than the
   *       node's id (pure function of ids → identical on every replica).
   * Parent genuinely absent → skip (B1): the node stays unplaced/invisible;
   * convergent, because every replica resolves parent-absent identically.
   * Parent present but TOMBSTONED → the node is placed but born tombstoned,
   * stamped with the parent's `removedAt`, and returned as undefined (see
   * `attach` below).
   */
  private recreateFromSpan(
    span: TreeRestoreSpan,
    offset: number,
    length: number,
  ): CRDTTreeNode | undefined {
    const parent = span.parentID
      ? this.findFloorNode(span.parentID)
      : undefined;
    if (!parent || (span.parentID && !parent.id.equals(span.parentID))) {
      // B1: parent gone (purged, and not part of this undo's spans). Leave
      // the node unplaced; a later parent-restore will bring it back.
      return;
    }

    const node = span.isText
      ? new CRDTTreeNode(
          CRDTTreeNodeID.of(span.id.getCreatedAt(), offset),
          span.nodeType,
          span.value!.substring(
            offset - span.id.getOffset(),
            offset - span.id.getOffset() + length,
          ),
        )
      : new CRDTTreeNode(
          span.id,
          span.nodeType,
          undefined,
          span.attrs?.deepcopy(),
        );

    // The span's attributes are a deep copy of the node's RHT, tombstones
    // included -- they have to be, or a recreated node would resolve a
    // concurrent style differently from a replica that never lost it. Each
    // copied tombstone is a fresh piece of garbage that no removal path
    // produced: without a registration it sits in the RHT forever, uncounted
    // and unpurgeable, and `getDataSize` excludes it so the node's own charge
    // does not cover it either. `getGCPairs` marks each `gcOnlySize`, which is
    // what sends it to gc alone. Booked by `attach` for both a live and a
    // removed parent, as Go's `recreateFromSpan` does.
    const recreatedAttrPairs = span.isText ? [] : node.getGCPairs();

    const siblings = parent.allChildren;

    // `attach` finishes every anchor rung below: register the node, then decide
    // whether it is born live or born tombstoned.
    //
    // The parent may have been tombstoned since this node was purged, in which
    // case the node is born tombstoned rather than live. This mirrors the
    // convention the concurrent-insert path already states ("if insertion
    // happens during concurrent editing and parent node has been removed, make
    // new nodes as tombstone immediately"). Recreating it live would leave a
    // node that the next purge of the parent unlinks but never unregisters --
    // reachable from nothing, still in nodeMapByID -- which is what sends
    // toTreePos off the top of a detached subtree (#2008).
    //
    // The stamp is the PARENT's removedAt, not the restoring operation's
    // ticket: that is the ticket the removal already wrote onto every sibling
    // it swept, so the node rejoins them carrying what it would have carried
    // had it never been purged, and it agrees with a replica that recreated it
    // live and then had the removal sweep it. Stamping the restoring op's
    // ticket makes those two disagree, and removedAt feeds canDelete, so they
    // would then collect on different passes. The stamp only has to be
    // overwritable by the eventual LWW winner, and it is: a later concurrent
    // removal with a higher ticket reaches the tombstoned node and overwrites
    // it (see CRDTTreeNode.remove).
    //
    // Accounting: the node is garbage the moment it is born, and its size was
    // never in `docSize.live` -- it was purged before this restore, and
    // returning undefined keeps it out of `restore`'s `recreated` list, which
    // is the only thing that books a recreated node into live. So the pair
    // carries `gcOnlySize`: charge the size to `docSize.gc` only and leave live
    // alone. Subtracting from live, as a pair without `gcOnlySize` would, drives
    // live negative by exactly this node's size.
    // Go's `RegisterGCPair` takes the same `GCOnlySize`, so both SDKs reach
    // the same numbers the same way. `purge` subtracts `child.getDataSize()`
    // from gc, so charging exactly that nets gc back to zero on collection.
    const attach = (): CRDTTreeNode | undefined => {
      this.registerNode(node);
      for (const pair of recreatedAttrPairs) {
        this.pendingGCPairs.push(pair);
      }
      if (parent.isRemoved) {
        node.remove(parent.removedAt!);
        this.pendingGCPairs.push({
          parent: this,
          child: node,
          gcOnlySize: node.getDataSize(),
        });
        return undefined;
      }
      return node;
    };

    // (a) same-insertion successor / predecessor piece (text): exact slot.
    if (span.isText) {
      const succ = this.findFloorNode(
        CRDTTreeNodeID.of(span.id.getCreatedAt(), offset + length),
      );
      if (
        succ &&
        succ.isText &&
        succ.parent === parent &&
        succ.id.getOffset() === offset + length
      ) {
        parent.insertAt(node, siblings.indexOf(succ));
        return attach();
      }
      if (offset > span.id.getOffset() || offset > 0) {
        const pred = this.findFloorNode(
          CRDTTreeNodeID.of(span.id.getCreatedAt(), offset - 1),
        );
        if (pred && pred.isText && pred.parent === parent) {
          parent.insertAfter(node, pred);
          return attach();
        }
      }
    }

    // (b) captured left boundary sibling, if it still exists under this parent.
    if (span.leftSiblingID) {
      const left = this.findFloorNode(span.leftSiblingID);
      if (left && left.parent === parent) {
        parent.insertAfter(node, left);
        return attach();
      }
    }

    // (c) captured right boundary sibling (redundant anchor): insert before it.
    if (span.rightSiblingID) {
      const right = this.findFloorNode(span.rightSiblingID);
      if (right && right.parent === parent) {
        const rightIdx = siblings.indexOf(right);
        parent.insertAt(node, rightIdx);
        return attach();
      }
    }

    // (d) deterministic id-order fallback: first slot whose child id > node id.
    let insertIdx = siblings.length;
    for (let i = 0; i < siblings.length; i++) {
      if (this.compareNodeID(siblings[i].id, node.id) > 0) {
        insertIdx = i;
        break;
      }
    }
    parent.insertAt(node, insertIdx);
    return attach();
  }

  /**
   * `leftAnchorID` returns the id to store as a restore span's left-sibling
   * anchor. For a text node the anchor is its LAST character's offset, not its
   * start: a concurrent delete may later split the left neighbor, and only the
   * last-char offset floor-resolves to the rightmost fragment (the true left
   * neighbor of the restored node). For elements (never split by offset) the
   * node's own id is exact. Right-sibling anchors always use the start offset,
   * which floor-resolves to the leftmost fragment — the true right neighbor.
   */
  private leftAnchorID(sibling: CRDTTreeNode): CRDTTreeNodeID {
    // A text node with no characters has no last character to anchor on, and
    // `value.length - 1` would put the anchor one code unit before the node's
    // own start — offset -1 for a node at offset 0. Local edits cannot create
    // one (`validateTextNode`), but a remote peer's contents are decoded
    // without that check. The node's own ID floor-resolves to the same node.
    if (!sibling.isText || sibling.value.length === 0) {
      return sibling.id;
    }
    return CRDTTreeNodeID.of(
      sibling.id.getCreatedAt(),
      sibling.id.getOffset() + sibling.value.length - 1,
    );
  }

  /**
   * `compareNodeID` totally orders two ids: createdAt (lamport, then actorID)
   * then offset. Used by the deterministic id-order restore fallback so a
   * recreated node lands in the same slot on every replica.
   */
  private compareNodeID(a: CRDTTreeNodeID, b: CRDTTreeNodeID): number {
    const c = a.getCreatedAt().compare(b.getCreatedAt());
    if (c !== 0) return c;
    return a.getOffset() - b.getOffset();
  }

  /**
   * `getGCPairs` returns the pairs of GC.
   */
  public getGCPairs(): Array<GCPair> {
    const pairs: Array<GCPair> = [];
    // NOTE: `traverse` only visits visible children, which never includes
    // removed nodes. `traverseAll` is required to register tombstones
    // (including pieces split off a tombstoned node) when the tree is
    // registered: snapshot load, a Set/Add/ArraySet payload, an undo re-set.
    // These pairs carry `gcOnlySize` because the tree's registered live size
    // only counted visible nodes.
    this.indexTree.traverseAll((node) => {
      // The walk includes the root, and purging is detachment from a parent,
      // which the root does not have. It is never legitimately removed, but a
      // crafted Set/Add payload can mark it so; leave it unbooked, as Go does.
      if (node.getRemovedAt() && node.parent) {
        pairs.push({
          parent: this,
          child: node,
          gcOnlySize: node.getDataSize(),
        });
      }

      for (const p of node.getGCPairs()) {
        pairs.push(p);
      }
    });

    return pairs;
  }

  /**
   * `findPos` finds the position of the given index in the tree. It rejects
   * an index inside a UTF-16 surrogate pair.
   */
  public findPos(index: number, preferText = true): CRDTTreePos {
    const treePos = this.indexTree.findTreePos(index, preferText);
    if (treePos.node.isText) {
      this.validateUTF16Boundary(treePos);
    }

    return CRDTTreePos.fromTreePos(treePos);
  }

  /**
   * `validateUTF16Boundary` throws when the given text position splits a
   * surrogate pair. At either end of the node it reads the neighbouring text
   * node: converting a position back to an index splits text nodes with no
   * operation, so a pair can sit in two nodes on this replica while it is
   * one node on every other, and an index at that seam is still inside it.
   */
  private validateUTF16Boundary({ node, offset }: TreePos<CRDTTreeNode>) {
    const value = node.value;
    let before = value.charCodeAt(offset - 1);
    let after = value.charCodeAt(offset);
    if (offset === 0) {
      const prev = node.prevSibling;
      before = prev?.isText
        ? prev.value.charCodeAt(prev.value.length - 1)
        : NaN;
    }
    if (offset === value.length) {
      const next = node.nextSibling;
      after = next?.isText ? next.value.charCodeAt(0) : NaN;
    }

    ensureUTF16Boundary(before, after);
  }

  /**
   * `findPosUnchecked` is `findPos` without the surrogate pair check. It is
   * for indexes the document computed itself, such as an undo range
   * reconciled against a remote edit, which can land inside a pair through no
   * fault of the caller. Refusing such an index would only drop the undo, so
   * it resolves the way it did before the check existed.
   */
  public findPosUnchecked(index: number, preferText = true): CRDTTreePos {
    const treePos = this.indexTree.findTreePos(index, preferText);
    return CRDTTreePos.fromTreePos(treePos);
  }

  /**
   * `pathToPosRange` converts the given path of the node to the range of the position.
   *
   * The end index is derived here rather than supplied by the caller, so it
   * goes through `findPosUnchecked`: the caller named a node by path, and
   * refusing the range because the index one past that node happens to land
   * inside a surrogate pair would reject a request that is valid as given.
   */
  public pathToPosRange(path: Array<number>): [CRDTTreePos, CRDTTreePos] {
    const fromIdx = this.pathToIndex(path);
    return [this.findPos(fromIdx), this.findPosUnchecked(fromIdx + 1)];
  }

  /**
   * `pathToPos` finds the position of the given index in the tree by path.
   */
  public pathToPos(path: Array<number>): CRDTTreePos {
    const index = this.indexTree.pathToIndex(path);
    return this.findPos(index);
  }

  /**
   * `getRoot` returns the root node of the tree.
   */
  public getRoot(): CRDTTreeNode {
    return this.indexTree.getRoot();
  }

  /**
   * `getSize` returns the size of the tree.
   */
  public getSize(): number {
    return this.indexTree.size;
  }

  /**
   * `getNodeSize` returns the size of the LLRBTree.
   */
  public getNodeSize(): number {
    return this.nodeMapByID.size();
  }

  /**
   * `getIndexTree` returns the index tree.
   */
  public getIndexTree(): IndexTree<CRDTTreeNode> {
    return this.indexTree;
  }

  /**
   * toXML returns the XML encoding of this tree.
   */
  public toXML(): string {
    return toXML(this.indexTree.getRoot());
  }

  /**
   * `getDataSize` returns the data usage of this element.
   */
  public getDataSize(): DataSize {
    const dataSize = { data: 0, meta: 0 };

    this.indexTree.traverse((node) => {
      if (node.getRemovedAt()) {
        return;
      }

      const size = node.getDataSize();
      dataSize.data += size.data;
      dataSize.meta += size.meta;
    });

    return {
      data: dataSize.data,
      meta: dataSize.meta + this.getMetaUsage(),
    };
  }

  /**
   * `toJSON` returns the JSON encoding of this tree.
   */
  public toJSON(): string {
    return JSON.stringify(this.getRootTreeNode());
  }

  /**
   * `toJSForTest` returns value with meta data for testing.
   */
  public toJSForTest(): Devtools.JSONElement {
    return {
      createdAt: this.getCreatedAt().toTestString(),
      value: JSON.parse(this.toJSON()),
      type: 'YORKIE_TREE',
    };
  }

  /**
   * `toJSInfoForTest` returns detailed TreeNode information for use in Devtools.
   */
  public toJSInfoForTest(): Devtools.TreeNodeInfo {
    const rootNode = this.indexTree.getRoot();

    const toTreeNodeInfo = (
      node: CRDTTreeNode,
      parentNode: CRDTTreeNode | undefined = undefined,
      leftChildNode: CRDTTreeNode | undefined = undefined,
      depth = 0,
    ): Devtools.TreeNodeInfo => {
      let index, path, pos;

      const treePos = node.isText
        ? { node, offset: 0 }
        : parentNode && leftChildNode
          ? this.toTreePos(parentNode, leftChildNode)
          : null;

      if (treePos) {
        index = this.indexTree.indexOf(treePos);
        path = this.indexTree.treePosToPath(treePos);
        pos = CRDTTreePos.fromTreePos(treePos).toStruct();
      }

      const nodeInfo: Devtools.TreeNodeInfo = {
        type: node.type,
        parent: parentNode?.id.toTestString(),
        size: node.visibleSize,
        id: node.id.toTestString(),
        removedAt: node.removedAt?.toTestString(),
        insPrev: node.insPrevID?.toTestString(),
        insNext: node.insNextID?.toTestString(),
        value: node.isText ? node.value : undefined,
        isRemoved: node.isRemoved,
        children: [] as Array<Devtools.TreeNodeInfo>,
        depth,
        attributes: node.attrs
          ? parseObjectValues(node.attrs?.toObject())
          : undefined,
        index,
        path,
        pos,
      };

      for (let i = 0; i < node.allChildren.length; i++) {
        const leftChildNode = i === 0 ? node : node.allChildren[i - 1];
        nodeInfo.children.push(
          toTreeNodeInfo(node.allChildren[i], node, leftChildNode, depth + 1),
        );
      }

      return nodeInfo;
    };

    return toTreeNodeInfo(rootNode);
  }

  /**
   * `getRootTreeNode` returns the converted value of this tree to TreeNode.
   */
  public getRootTreeNode(): TreeNode {
    return toTreeNode(this.indexTree.getRoot());
  }

  /**
   * `toTestTreeNode` returns the JSON of this tree for debugging.
   */
  public toTestTreeNode(): TreeNodeForTest {
    return toTestTreeNode(this.indexTree.getRoot());
  }

  /**
   * `toSortedJSON` returns the sorted JSON encoding of this tree.
   */
  public toSortedJSON(): string {
    return this.toJSON();
  }

  /**
   * `deepcopy` copies itself deeply.
   */
  public deepcopy(): CRDTTree {
    const root = this.getRoot();
    const tree = new CRDTTree(root.deepcopy(), this.getCreatedAt());
    tree.setRemovedAt(this.getRemovedAt());
    tree.setMovedAt(this.getMovedAt());
    return tree;
  }

  /**
   * `toPath` converts the given CRDTTreeNodeID to the path of the tree.
   */
  public toPath(
    parentNode: CRDTTreeNode,
    leftNode: CRDTTreeNode,
  ): Array<number> {
    const treePos = this.toTreePos(parentNode, leftNode);
    if (!treePos) {
      return [];
    }

    return this.indexTree.treePosToPath(treePos);
  }

  /**
   * `toIndex` converts the given CRDTTreeNodeID to the index of the tree.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  public toIndex(
    parentNode: CRDTTreeNode,
    leftNode: CRDTTreeNode,
    includeRemoved: boolean = false,
  ): number {
    const treePos = this.toTreePos(parentNode, leftNode, includeRemoved);
    if (!treePos) {
      return -1;
    }

    return this.indexTree.indexOf(treePos, includeRemoved);
  }

  /**
   * `indexToPath` converts the given tree index to path.
   */
  public indexToPath(index: number): Array<number> {
    return this.indexTree.indexToPath(index);
  }

  /**
   * `pathToIndex` converts the given path to index.
   */
  public pathToIndex(path: Array<number>): number {
    return this.indexTree.pathToIndex(path);
  }

  /**
   * `indexRangeToPosRange` returns the position range from the given index range.
   * It converts a selection, not an edit range, so it skips the surrogate pair
   * check.
   */
  public indexRangeToPosRange(range: [number, number]): TreePosRange {
    const fromPos = this.findPosUnchecked(range[0]);
    if (range[0] === range[1]) {
      return [fromPos, fromPos];
    }
    return [fromPos, this.findPosUnchecked(range[1])];
  }

  /**
   * `indexRangeToPosStructRange` converts the integer index range into the Tree position range structure.
   * Like `indexRangeToPosRange`, it is for selections and skips the surrogate
   * pair check.
   */
  public indexRangeToPosStructRange(
    range: [number, number],
  ): TreePosStructRange {
    const [fromIdx, toIdx] = range;
    const fromPos = this.findPosUnchecked(fromIdx);
    if (fromIdx === toIdx) {
      return [fromPos.toStruct(), fromPos.toStruct()];
    }

    return [fromPos.toStruct(), this.findPosUnchecked(toIdx).toStruct()];
  }

  /**
   * `posRangeToPathRange` converts the given position range to the path range.
   */
  public posRangeToPathRange(
    range: TreePosRange,
  ): [Array<number>, Array<number>] {
    const [[fromParent, fromLeft]] = this.findNodesAndSplitText(range[0]);
    const [[toParent, toLeft]] = this.findNodesAndSplitText(range[1]);
    return [this.toPath(fromParent, fromLeft), this.toPath(toParent, toLeft)];
  }

  /**
   * `posRangeToIndexRange` converts the given position range to the path range.
   */
  public posRangeToIndexRange(range: TreePosRange): [number, number] {
    const [[fromParent, fromLeft]] = this.findNodesAndSplitText(range[0]);
    const [[toParent, toLeft]] = this.findNodesAndSplitText(range[1]);
    return [this.toIndex(fromParent, fromLeft), this.toIndex(toParent, toLeft)];
  }

  /**
   * `traverseInPosRange` traverses the tree in the given position range.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  private traverseInPosRange(
    fromParent: CRDTTreeNode,
    fromLeft: CRDTTreeNode,
    toParent: CRDTTreeNode,
    toLeft: CRDTTreeNode,
    callback: (token: TreeToken<CRDTTreeNode>, ended: boolean) => void,
    includeRemoved: boolean = false,
  ): void {
    const fromIdx = this.toIndex(fromParent, fromLeft, includeRemoved);
    const toIdx = this.toIndex(toParent, toLeft, includeRemoved);
    // When a concurrent merge redirects the to-position into an earlier
    // part of the tree, the range becomes empty (prior merge handled it).
    if (fromIdx > toIdx) {
      return;
    }
    this.indexTree.tokensBetween(fromIdx, toIdx, callback, includeRemoved);
  }

  /**
   * `toTreePos` converts the given nodes to the position of the IndexTree.
   * If includeRemoved is true, it includes removed nodes in the calculation.
   */
  private toTreePos(
    parentNode: CRDTTreeNode,
    leftNode: CRDTTreeNode,
    includeRemoved: boolean = false,
  ): TreePos<CRDTTreeNode> | undefined {
    if (!parentNode || !leftNode) {
      return;
    }

    if (!includeRemoved && parentNode.isRemoved) {
      // If parentNode is removed, treePos is the position of its least alive ancestor.
      let childNode: CRDTTreeNode;
      while (parentNode.isRemoved) {
        childNode = parentNode;
        // If the subtree has been detached by garbage collection, the walk
        // can run off the top of it. Report it instead of dereferencing
        // undefined. Throwing rather than returning undefined is deliberate:
        // toIndex turns undefined into -1, which would go on to resolve a
        // bogus position and edit the wrong range.
        if (!childNode.parent) {
          throw new YorkieError(
            Code.ErrInvalidArgument,
            `least alive ancestor of ${childNode.id.toIDString()}: node not found`,
          );
        }
        parentNode = childNode.parent;
      }

      const offset = parentNode.findOffset(childNode!, includeRemoved);
      return {
        node: parentNode,
        offset,
      };
    }

    if (parentNode === leftNode) {
      return {
        node: parentNode,
        offset: 0,
      };
    }

    // Find the closest existing leftSibling node.
    let offset = parentNode.findOffset(leftNode, includeRemoved);
    if (includeRemoved || !leftNode.isRemoved) {
      if (leftNode.isText) {
        return {
          node: leftNode,
          offset: leftNode.paddedSize(includeRemoved),
        };
      }

      offset++;
    }

    return {
      node: parentNode,
      offset,
    };
  }

  /**
   * `makeDeletionChanges` converts nodes to be deleted to deletion changes.
   */
  private makeDeletionChanges(
    candidates: Array<TreeToken<CRDTTreeNode>>,
    editedAt: TimeTicket,
  ): Array<TreeChange> {
    const changes: Array<TreeChange> = [];
    const ranges: Array<Array<TreeToken<CRDTTreeNode>>> = [];

    // Generate ranges by accumulating consecutive nodes.
    let start = null;
    let end = null;
    for (let i = 0; i < candidates.length; i++) {
      const cur = candidates[i];
      const next = candidates[i + 1];
      if (!start) {
        start = cur;
      }
      end = cur;

      const rightToken = this.findRightToken(cur);
      if (
        !rightToken ||
        !next ||
        rightToken[0] !== next[0] ||
        rightToken[1] !== next[1]
      ) {
        ranges.push([start, end]);
        start = null;
        end = null;
      }
    }

    // Convert each range to a deletion change.
    for (const range of ranges) {
      const [start, end] = range;
      const [fromLeft, fromLeftTokenType] = this.findLeftToken(start);
      const [toLeft, toLeftTokenType] = end;
      const fromParent =
        fromLeftTokenType === TokenType.Start ? fromLeft : fromLeft.parent!;
      const toParent =
        toLeftTokenType === TokenType.Start ? toLeft : toLeft.parent!;

      const fromIdx = this.toIndex(fromParent, fromLeft);
      const toIdx = this.toIndex(toParent, toLeft);
      if (fromIdx < toIdx) {
        // When the range is overlapped with the previous one, compact them.
        if (changes.length > 0 && fromIdx === changes[changes.length - 1].to) {
          changes[changes.length - 1].to = toIdx;
          changes[changes.length - 1].toPath = this.toPath(toParent, toLeft);
        } else {
          changes.push({
            type: TreeChangeType.Content,
            from: fromIdx,
            to: toIdx,
            fromPath: this.toPath(fromParent, fromLeft),
            toPath: this.toPath(toParent, toLeft),
            actor: editedAt.getActorID(),
          });
        }
      }
    }
    return changes.reverse();
  }

  /**
   * `findRightToken` returns the token to the right of the given token in the tree.
   */
  private findRightToken([
    node,
    tokenType,
  ]: TreeToken<CRDTTreeNode>): TreeToken<CRDTTreeNode> {
    if (tokenType === TokenType.Start) {
      const children = node.allChildren;
      if (children.length > 0) {
        return [
          children[0],
          children[0].isText ? TokenType.Text : TokenType.Start,
        ];
      }
      return [node, TokenType.End];
    }

    const parent = node.parent!;
    const siblings = parent.allChildren;
    const offset = siblings.indexOf(node);
    if (parent && offset === siblings.length - 1) {
      return [parent, TokenType.End];
    }

    const next = siblings[offset + 1];
    return [next, next.isText ? TokenType.Text : TokenType.Start];
  }

  /**
   * `findLeftToken` returns the token to the left of the given token in the tree.
   */
  private findLeftToken([
    node,
    tokenType,
  ]: TreeToken<CRDTTreeNode>): TreeToken<CRDTTreeNode> {
    if (tokenType === TokenType.End) {
      const children = node.allChildren;
      if (children.length > 0) {
        const lastChild = children[children.length - 1];
        return [lastChild, lastChild.isText ? TokenType.Text : TokenType.End];
      }

      return [node, TokenType.Start];
    }

    const parent = node.parent!;
    const siblings = parent.allChildren;
    const offset = siblings.indexOf(node);
    if (parent && offset === 0) {
      return [parent, TokenType.Start];
    }

    const prev = siblings[offset - 1];
    return [prev, prev.isText ? TokenType.Text : TokenType.End];
  }
}
